/**
 * Creative Studio — React rebuild of the production monolith's `.es` email
 * builder overlay, MVP-only since the 2026-08-09 cutover: no left block palette
 * and no blank canvas (monolith html.mvpaud) — customize/move only. Dark
 * full-screen surface: top bar (device toggle + zoom + Done), a 600px "paper"
 * canvas that renders each block's real email HTML in an `.esmod` wrapper with
 * per-block hover controls (move / duplicate / delete / select), the "+" gap
 * inserter (restricted to MVP_BLOCKS), and the block Inspector.
 *
 * The email markup comes only from the pure-TS renderers in src/email/ (parity-
 * gated); React owns the editing chrome, never the email HTML.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./creative-studio.css";
import { blockDef, blockLabel } from "../email/blocks";
import { isDisclaimerBlock, footerWithDisclaimer } from "../services/disclaimerLive";
import { imgSizeKeys, imgSizeSlot, labelWithSize, markupImgWidth } from "../services/imgSize";
import { LAY_STASH } from "../services/echLayout";
import { isBareShell, renderDocument, type AnyBlockInstance } from "../email/document";
import { sendTimeViewRow } from "../services/echSendTimeView";
import Inspector, { type BynderPickAsset, type OpenBynder } from "../components/Inspector";
import TemplateEditor, { type TplSection, type TplEditorApi } from "../components/TemplateEditor";
import { capsByLabel } from "../services/echResendCaps";
import { favSave, searchCommunities } from "../services/api";
import { type SfmcBlock } from "../services/sfmcBlocks";
import { useEchShell } from "../services/useEchShell";
import { sfmcBlocksOnce, sfmcBlocksWatch } from "../services/sfmcSource";
import { grFor, patternRulesFor, echVersion, echNameOf, echMeta, echAmenLib, echBlocksMeta, echByName } from "../services/echRegistry";
import { m2BlocksEnabled, paletteGroups, freeMove, m2FamilyOf, m2OnceViolation, m2IsButtonBlock } from "../services/m2Blocks";
import M2Palette from "../components/M2Palette";
import { echAsmOf, echDoc, echStamp, echTplFor, echRebuildKeepEdits, echTplVersion, echTemplateNames, echSeq, ECH_TPLDEF } from "../services/echTemplates";
import { m2TplRules, m2LockedIds, m2CanMove, m2ClampInsert, m2PaletteOffers, m2FamilyDefault, m2NeedsCompanion, m2DropOrphanHeads, m2Respace, m2ModulesBlocked, m2RunEnd, m2PanelFull, m2SameLocked, M2_LOCKED_MSG, type M2TplRules } from "../services/m2Rules";
import { echAdopt, echLibReady } from "../services/echSnap";
import { echContactWarnings, echPacks, echPickCodes, echClaimPatch, socIsOff, socOffList, ECH_FEAT, type DynBlock, type EchCtx } from "../services/echDyn";
import { echCtaLinkWarnings } from "../services/echCtaLink";
import { cardFrame, cdnCropReady, probeCdnCrop } from "../services/cropCardPhotos";
import { isCdnPhoto } from "../services/echBynderMatch";
/* the page-level promise cache — the wizard pulls its packs through the same one, so
   re-resolving a stored pick on load is a cache hit and not a second request */
import { feedCommunityPack } from "../services/echFeeds";
import type { Pack } from "../services/dynamicFill";
import { buildManifest, stampManifest, type Notice } from "../services/manifest";
import { useEchDyn, type Step1Scope } from "../services/useEchDyn";
import { useAmenityIcons } from "../services/useAmenityIcons";
import { amenFolderVersion, amenPairs, amenPicked } from "../services/echAmen";
import { useEchDocFill } from "../services/useEchDocFill";
import { echCarryLabels, echPinOnDelete } from "../services/echSection";
import { amenEmptyNote } from "../services/echAmenNote";
import { violations, limitMessage, type Violation } from "../services/guardrails";
import { grMeta } from "../services/echRegistry";
import {
  EK_ATTR, ZW, ekTarget, inlineClean, inlineCommit, inlineDisplay, inlineLocked, inlineTargets,
  resolveBind, tagEditable, type InlineTarget,
} from "../services/echInline";
import { rowOf } from "../services/echRows";
import { mobileEmulationCss, shellCanvasCss } from "../services/echMobile";
import { requiredBlanks, type RequiredBlank } from "../services/echRequired";
import { dataGateFindings, dataGatePending } from "../services/echDataGate";
import { controlState } from "../services/echControls";
import type { CapBlock } from "../services/blockCaps";
import { insertBlocked, lockedIds, orphanedHeaders, reorder, type PlaceBlock } from "../services/echPlacement";
import { capViolation } from "../services/blockCaps";
import { type AiCopyResult } from "../components/AiCopyModal";
import { ProofBarRight, ProofCompareSeg, ProofInspector, ProofPins, ProofStrip, ProofVersionCanvas, dropAt, toggleBlockHl, useProofUi, type ProofCtx } from "./proof/ProofStudio";
import type { CopyContext } from "../services/aiCopy";

/** a background block update waits until nothing has been typed for this long */
const WATCH_QUIET_MS = 4000;

interface Props {
  blocks: AnyBlockInstance[];
  /** structural ops (add/move/dup/del/insert/reorder) — recorded in undo history */
  setBlocks: (updater: (b: AnyBlockInstance[]) => AnyBlockInstance[]) => void;
  /** field edits — live, not history-recorded */
  /** `history: false` marks a DERIVED write (the fill) that must not enter undo (#131) */
  onPatch: (id: string, patch: Record<string, unknown>, opts?: { history?: boolean }) => void;
  makeBlock: (type: string) => AnyBlockInstance;
  /** open the Bynder picker; the pick lands wherever `apply` routes it (plain
      field, repeat-item field, or a film-strip append — monolith __bynderPick) */
  onOpenBynder: OpenBynder;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** monolith `esResetHist()` — template assembly replaces the whole document, so
   *  the pre-assembly starter doc must not sit in the undo stack (L9204) */
  onResetHistory?: () => void;
  /** edit-real-template mode: loaded template HTML + edited output (App owns these; builtHtml = tplEdited || blocks) */
  tplHtml: string;
  /** the CURRENT edited template doc — export/copy/save must hand over the
   *  user's edits, never the original load (Jack 2026-08-09: features must work) */
  tplEdited: string;
  setTplEdited: (h: string) => void;
  onClose: () => void;
  /** Studio-scope AI copy (monolith esAIcopy → __aiCopyStudio full scope) */
  aiCtx: () => CopyContext;
  onAiApply: (r: AiCopyResult) => void;
  /** ★ Save favorite needs the division + a default name (monolith esFav) */
  division?: string;
  projectName?: string;
  /**
   * NAMED DRAFTS + PREVIEW LINK (2026-09-18, pilot item 1 — see services/namedDrafts.ts).
   * App owns the document, so App does the saving; the Studio only asks for a name
   * and shows the result. `current` is the draft this canvas was opened from (or last
   * saved as), so "Copy preview link" re-saves under the same name and the link stays
   * put. Absent (production today) → neither menu item renders.
   */
  namedDrafts?: {
    current: { name: string; previewUrl: string } | null;
    save: (name: string, html: string) => Promise<{ ok: boolean; previewUrl?: string; error?: string }>;
  };
  /** Step 1's scope — what every dynamic ECH block fills FROM (audit row 19) */
  step1: Step1Scope;
  /**
   * THIS IS A RESEND OF A FINISHED EMAIL (2026-08-31). Jack, on FNO pilot feedback:
   * "Should we allow resends for templates we don't have locked down? There were no
   * character limits and I could change icons."
   *
   * A resend hands the template editor an opaque document with no block types on it, so
   * nothing was enforced — it was the one way to get an uncapped, icon-swappable email.
   * When true the editor is given the declared caps projected onto its own shape labels
   * (`capsByLabel`) and the artwork is locked. Copy stays editable either way.
   */
  resend?: boolean;
  /**
   * The template name to ASSEMBLE from Layne's SFMC blocks (audit row 23).
   *
   * "" opts out, which is how a bespoke document reaches the Studio untouched: a
   * sent email ("As sent · …"), a division favorite ("★ …") or a community-filled
   * build is not a standard template and must stay in the template editor. App
   * decides that — see the `echTplName` prop it passes.
   */
  echTplName?: string;
  /**
   * What changed between the original send and now, when this document arrived as a
   * DUPLICATE (`services/manifest.duplicateFromHtml`). Empty for every other way in.
   *
   * Shown above the canvas because that is what it describes — a cap that tightened,
   * a field Layne removed, markup that moved on, or the delivered HTML disagreeing
   * with the request that produced it. It is a read-out, not a prompt: per Jack, the
   * requester never chooses between recovery methods, so there is nothing here to
   * accept or reject, only to look at.
   */
  dupNotices?: Notice[];
  onClearDupNotices?: () => void;
  /**
   * PROOFING (Milestone 2, 2026-10-06). Present = this Studio is a submitted job's review
   * screen: the same Design Email shell with comments on it (Jack's Oct 5 mockup).
   * `comment` = a division reviewer — the email is read-only, a click drops a pin.
   * `edit` = Digital Ops — the usual block editing, plus comments, rounds and Save revision.
   */
  proof?: ProofCtx;
  /** Milestone 2 panel: the template selector at the top of the left panel picks the
   *  form's template, the same field Step 1 sets (monolith #esTplSel). */
  onPickTemplate?: (name: string) => void;
  /** the disclaimer Step 4 would build right now (App → services/disclaimerLive). Drawn into
   *  the footer block so Design Email shows the real text, not Layne's placeholder. [] = leave it. */
  disclaimerParas?: string[];
  /** true ONLY on the markup Edit Email link: the footer's panel becomes a text box whose
   *  value is saved on the footer block (`__dsText`) and wins over the built text. Nowhere
   *  else can the disclaimer be typed over (Jack, 2026-10-19). */
  disclaimerEditable?: boolean;
}

let sid = 1;
const newId = () => "s" + sid++;

/* ── INLINE-EDIT DOM HELPERS ───────────────────────────────────────────────────
   The editable node lives inside a `dangerouslySetInnerHTML` subtree, so React
   owns none of it — these are the monolith's own caret primitives (`__grCaretEnd`
   and the offset preservation added at L12470–12496), lifted as plain functions.

   WHY THE CARET IS PRESERVED AT THE CAP AND NOT JUST SENT TO THE END: sending it
   to the end on every keystroke once a field is at its limit relocates where you
   are typing and scatters characters — that is the mechanism behind the garbling
   Jack reported on the monolith (comment at L12470). Hitting a cap must simply
   refuse the extra character. */

/** what the user sees in the node — `innerText` where it exists (it honours the
 *  `<br>`s `escML` emits), `textContent` in jsdom, ZWSP stripped either way */
function readText(el: HTMLElement): string {
  const t = (el as HTMLElement & { innerText?: string }).innerText;
  return inlineClean(t == null ? (el.textContent ?? "") : t);
}
function caretEnd(el: HTMLElement): void {
  try {
    const doc = el.ownerDocument;
    const r = doc.createRange(); r.selectNodeContents(el); r.collapse(false);
    const s = doc.defaultView?.getSelection(); s?.removeAllRanges(); s?.addRange(r);
  } catch { /* jsdom / no selection API */ }
}
/** characters between the start of `el` and the caret, or null if unknowable */
function caretOffset(el: HTMLElement): number | null {
  try {
    const s = el.ownerDocument.defaultView?.getSelection();
    if (!s || !s.rangeCount) return null;
    const live = s.getRangeAt(0);
    const rg = live.cloneRange();
    rg.selectNodeContents(el);
    rg.setEnd(live.endContainer, live.endOffset);
    return rg.toString().length;
  } catch { return null; }
}
function caretTo(el: HTMLElement, pos: number): void {
  try {
    const doc = el.ownerDocument;
    const w = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    let seen = 0, tn: Node | null;
    while ((tn = w.nextNode())) {
      const len = (tn.nodeValue || "").length;
      if (seen + len >= pos) {
        const r = doc.createRange(); r.setStart(tn, Math.max(0, pos - seen)); r.collapse(true);
        const s = doc.defaultView?.getSelection(); s?.removeAllRanges(); s?.addRange(r);
        return;
      }
      seen += len;
    }
  } catch { /* fall through */ }
  caretEnd(el);
}

/** one live inline edit — the node, the field it is bound to, and its listeners */
interface InlineEdit {
  id: string;
  t: InlineTarget;
  el: HTMLElement;
  /** the node's text as it was before the edit — what Escape puts back */
  orig: string;
  onInput: () => void;
  onKey: (e: KeyboardEvent) => void;
  onBlur: () => void;
}

/* The "+" insert point between blocks (the Gap component and its grouped block
   menu) was REMOVED per Jack, 2026-08-21: "should not be able to add any
   additional blocks like this — remove this feature and ensure this isn't
   happening on any templates." A template is Layne's block ORDER as much as her
   block markup — a division inserting arbitrary blocks breaks the template
   contract on every one of the 5 MVP templates. What remains is deliberate:
   drag-to-REORDER (stageDrop is moves-only), duplicate/delete on the block bar,
   and the Featured section's capped + Add card (Inspector onAddBelow — a
   controlled same-type add, not a free menu). */

/** one block's email HTML, wrapped in the 600px table so a bare <tr> renders.
    An ECH type that has not registered yet (/sfmc-blocks still in flight, or a
    restored document) renders as nothing rather than taking the canvas down. */
const NO_PARAS: string[] = [];

function blockHtml(b: AnyBlockInstance): string {
  const def = blockDef(b.type);
  if (!def) return "";
  /* a send-time (AMPscript) block shows its placeholder on the canvas — display only */
  const row = sendTimeViewRow(b.type, (def.render as (d: unknown) => string)(b.data));
  return '<table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:100%;border-collapse:collapse"><tbody>' + row + "</tbody></table>";
}

/* ── TEMPLATE SECTION inspector (monolith esInsp for template sections — full
   inventory captured live in the 2026-08-09 audit doc). Manual pane is complete;
   the hero Dynamic pane renders the monolith's copy with the community picker
   stubbed until the community-pack wiring lands (flagged in the audit). ── */
const TPL_NOTE = "Type on the email to change wording. Use these fields for images and links.";
const TPL_BYN_HINT = "Images pull from Bynder — click the photo or “Select a photo.”";
function TplInspector({ sel, api, onOpenBynder, onRefresh, onClose, division }: {
  sel: TplSection;
  api: React.MutableRefObject<TplEditorApi | null>;
  onOpenBynder: (apply: (url: string, asset: BynderPickAsset) => void, need?: { w?: number; h?: number }) => void;
  onRefresh: (s: TplSection | null) => void;
  onClose: () => void;
  division: string;
}) {
  const [tab, setTab] = useState<"manual" | "dynamic">("manual");
  /* Dynamic pane — the monolith's community drill, backed by the same worker
     community search the form's auto-fill uses: pick a community, its live
     hero photo fills the section (2026-08-09 — was a dead disabled stub). */
  const [dynOpen, setDynOpen] = useState(false);
  const [dynQ, setDynQ] = useState("");
  const [dynHits, setDynHits] = useState<{ code: string; name: string; hero: string }[] | null>(null);
  const [dynBusy, setDynBusy] = useState(false);
  const [dynPicked, setDynPicked] = useState("");
  const dynSearch = (q: string) => {
    setDynQ(q);
    if (q.trim().length < 2) { setDynHits(null); return; }
    setDynBusy(true);
    searchCommunities(q.trim())
      .then((r) => setDynHits(((r as { hits?: { code: string; name: string; hero: string }[]; items?: { code: string; name: string; hero: string }[] }).hits || (r as { items?: { code: string; name: string; hero: string }[] }).items || [])))
      .catch(() => setDynHits([]))
      .finally(() => setDynBusy(false));
  };
  /* the monolith template image card: natural-aspect photo, URL row + Bynder
     button + note INSIDE the card (.imgc > .thumb / .imgc-url / .imgc-bd /
     .imgc-note — DOM captured live 2026-08-09) */
  const imgFlds = sel.imgs.map((im, i) => (
    <div className="fld" key={"im" + i}>
      <label>{sel.imgs.length > 1 ? "Image " + (i + 1) : "Image"}</label>
      <div className="tplimgc">
        <div className="thumb">{im.src ? <img src={im.src} alt="" /> : null}</div>
        <input className="imgc-url" value={im.src} placeholder="Pulled from Bynder"
          onChange={(e) => { const r = api.current?.img(sel.idx, i, e.target.value); if (r) onRefresh(r); }} />
        <div className="imgc-bd">
          <button type="button" className="imgc-main"
            onClick={() => onOpenBynder((url, asset) => { const r = api.current?.img(sel.idx, i, url, (asset && (asset.alt || asset.name)) || undefined); if (r) onRefresh(r); })}>📂 Select a photo</button>
        </div>
        <div className="imgc-note">{TPL_BYN_HINT}</div>
      </div>
    </div>
  ));
  /* link fields — text links get text+URL; a textless link (logo wrap) gets URL only */
  const many = sel.links.length > 1;
  const linkFlds = sel.links.flatMap((ln, i) => {
    const n = many ? " " + (i + 1) : "";
    const rows = [];
    if (ln.text) rows.push(
      <div className="fld" key={"lt" + i}>
        <label>{"Link" + n + " text"}</label>
        <input defaultValue={ln.text} onChange={(e) => { api.current?.link(sel.idx, i, "text", e.target.value); }} />
      </div>);
    rows.push(
      <div className="fld" key={"lu" + i}>
        <label>{"Link" + n + " URL"}</label>
        <input className="mono" defaultValue={ln.href} onChange={(e) => { api.current?.link(sel.idx, i, "href", e.target.value); }} />
      </div>);
    return rows;
  });
  return (
    <>
      <div className="ih">
        <span className="ihtx">{sel.hero ? "Hero image" : sel.label}<small>TEMPLATE SECTION</small></span>
        <button className="ihx" title="Hide panel" onClick={onClose}>✕</button>
      </div>
      <div className="ib">
        {sel.hero && (
          <div className="dynseg" style={{ marginBottom: 12 }}>
            <button type="button" className={tab === "manual" ? "on" : ""} onClick={() => setTab("manual")}>Manual</button>
            <button type="button" className={tab === "dynamic" ? "on" : ""} onClick={() => setTab("dynamic")}>Dynamic</button>
          </div>
        )}
        {sel.hero && tab === "dynamic" ? (
          <div className="dynpane">
            <div className="dynhead"><span>Images · {dynPicked ? "1 selected" : "0 selected"}</span><span>Communities ›</span></div>
            {!dynOpen ? (
              <button type="button" className="dyntile" onClick={() => setDynOpen(true)}>
                <span className="big">🏠</span>
                <span className="t">+ Select a hero image</span>
                <span className="s">pick a community — its hero photo fills in automatically</span>
              </button>
            ) : (
              <>
                <div className="fld"><input autoFocus value={dynQ} placeholder={"Search " + (division || "your") + " communities…"} onChange={(e) => dynSearch(e.target.value)} /></div>
                {dynBusy && <div className="imgc-note">Searching…</div>}
                {dynHits && !dynBusy && (
                  <div className="dynlist">
                    {dynHits.length === 0 && <div className="imgc-note">No communities match — try another name.</div>}
                    {dynHits.slice(0, 8).map((h) => (
                      <button type="button" key={h.code} disabled={!h.hero}
                        title={h.hero ? "" : "no hero photo in the live feed"}
                        onClick={() => { const r = api.current?.img(sel.idx, 0, h.hero, h.name); if (r) onRefresh(r); setDynPicked(h.name); }}>
                        {h.hero ? <img src={h.hero} alt="" /> : <span className="noimg" />}
                        <span>{h.name}{h.hero ? "" : " — no hero photo in the live feed"}</span>
                      </button>
                    ))}
                  </div>
                )}
                {dynPicked && <div className="imgc-note">✓ {dynPicked} — hero applied. Switch to Manual to fine-tune.</div>}
              </>
            )}
          </div>
        ) : (
          <>
            {!sel.hero && <div className="tplnote2">{TPL_NOTE}</div>}
            {imgFlds}
            {linkFlds}
          </>
        )}
        <button type="button" className="esdelf" onClick={() => { api.current?.remove(sel.idx); onRefresh(null); }}>Remove this section</button>
      </div>
    </>
  );
}

export default function StudioView({ blocks, setBlocks, onPatch, makeBlock, onOpenBynder, onUndo, onRedo, canUndo, canRedo, onResetHistory, tplHtml, tplEdited, setTplEdited, onClose, aiCtx, onAiApply, division, projectName, step1, resend, echTplName, dupNotices, onClearDupNotices, namedDrafts, proof, onPickTemplate, disclaimerParas, disclaimerEditable }: Props) {
  const pui = useProofUi();
  const pc = proof?.mode === "comment";
  const dsParas = disclaimerParas || NO_PARAS;
  const dsOverride = (b: AnyBlockInstance): string => String(((b.data || {}) as Record<string, unknown>).__dsText || "");
  const [selId, setSelId] = useState<string | null>(null);
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const [zoom, setZoom] = useState(1);
  const [fitOn, setFitOn] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  /* inspector gating (monolith insp-open): closed until a block is selected or
     the gold EDIT tab is clicked; × closes it without deselecting */
  /* proofing opens with the comments showing (the mockup's inspector is always there) */
  const [inspOpen, setInspOpen] = useState(!!proof);
  /* resizable inspector (monolith wireResize: 240–560/330-378 — the palette's
     left handle went with the palette in the MVP-only cutover) */
  const [inspW, setInspW] = useState<number>(() => { const v = parseInt(localStorage.getItem("esInspW") || "", 10); return v >= 240 && v <= 560 ? v : 330; /* monolith .esinsp width */ });
  const dragRes = useRef<{ startX: number; startW: number } | null>(null);
  useEffect(() => {
    const mm = (e: MouseEvent) => {
      const d = dragRes.current; if (!d) return;
      setInspW(Math.max(240, Math.min(560, d.startW - (e.clientX - d.startX))));
    };
    const mu = () => {
      if (!dragRes.current) return;
      dragRes.current = null;
      document.body.style.userSelect = ""; document.body.style.cursor = "";
      try {
        setInspW((w) => { localStorage.setItem("esInspW", String(w)); return w; });
      } catch { /* quota */ }
    };
    document.addEventListener("mousemove", mm);
    document.addEventListener("mouseup", mu);
    return () => { document.removeEventListener("mousemove", mm); document.removeEventListener("mouseup", mu); };
  }, []);
  const resStart = (e: React.MouseEvent) => {
    e.preventDefault();
    dragRes.current = { startX: e.clientX, startW: inspW };
    document.body.style.userSelect = "none"; document.body.style.cursor = "col-resize";
  };
  const [dragId, setDragId] = useState<string | null>(null);
  /* ── MILESTONE 2 TEST BED (services/m2Blocks.ts) ─────────────────────────────────
     `m2On` is read once per Studio mount: the left block palette, and guardrails off
     by default so every block can go anywhere (Layne's phase 1 — validate the build
     before deciding the rules). `free` is what every placement path below consults. */
  const m2On = useMemo(() => m2BlocksEnabled(), []);
  /* `free` (guardrails off) is now decided per template, below `echTpl` — phase 2 */
  const [palQ, setPalQ] = useState("");
  /** a palette chip being dragged onto the canvas (its block type) */
  const [palType, setPalType] = useState<string | null>(null);
  /* Blank canvas and All blocks were removed from the panel 2026-10-26 (Jack): every
     email starts from a template. */
  /** insertion index the .esdrop line is showing at (monolith stage._idx) */
  const [dropIdx, setDropIdx] = useState<number | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  /**
   * MOBILE MEDIA-QUERY EMULATION (2026-08-21). The 380px `.paper.mobile` is a
   * DIV, so the ECH blocks' locked `@media (max-width:480px)` stacking CSS
   * evaluates against the WINDOW and never fires in the preview — ECH_2CTA's
   * two fixed 200px buttons (460px min-content) then clip the paper (Jack's
   * screenshot: logo + second CTA cut off on Mobile). Fix in the frame, not
   * the blocks: read every <style> the rendered blocks brought with them,
   * rescope their mobile rules under `.es .paper.mobile`, and inject them as
   * one document-level sheet while the device is mobile. Export HTML is
   * untouched — this is preview behavior only. See services/echMobile.ts.
   */
  useEffect(() => {
    const ID = "ech-mobile-emu";
    if (device !== "mobile") { document.getElementById(ID)?.remove(); return; }
    /* the paper's OWN serialized markup: mobileEmulationCss reads its <style>
       tags for the media rules AND its <table> tags for the fixed-width fluid
       rules (ECH_Logo's width="600" header — Chrome floors a table at its
       specified width, so the clamp's max-width:100% alone cannot shrink it).
       Everything lands in this ONE head-level sheet: attribute selectors, so
       nothing is written into the rendered blocks — React 19 re-applies
       dangerouslySetInnerHTML on object identity and wipes DOM-added classes. */
    const paper = canvasRef.current?.querySelector(".paper");
    const css = mobileEmulationCss(paper ? paper.innerHTML : "");
    let el = document.getElementById(ID) as HTMLStyleElement | null;
    if (!el) { el = document.createElement("style"); el.id = ID; document.head.appendChild(el); }
    if (el.textContent !== css) el.textContent = css;
    return () => { document.getElementById(ID)?.remove(); };
  }, [device, blocks]);

  /**
   * SIZE THE BOX TO THE SCALED TEMPLATE (2026-08-20).
   *
   * `.espaper` is scaled with `transform: scale(zoom)` and a transform does not
   * affect layout, so the canvas keeps reserving the full un-scaled height and the
   * remainder paints as white below the footer — worse the further you zoom out.
   * This wrapper is given the painted size so the box IS the template.
   *
   * Derived from `offsetHeight × zoom`, NOT from `getBoundingClientRect()`. The rect
   * is the painted box, so reading it here would feed this function's own output back
   * into itself; offset* is the layout box and is unaffected by the transform. The
   * equality guard matters for the same reason — an unconditional write inside an
   * observer of the thing it changes is a render loop, and the first attempt at this
   * fix caused exactly that.
   *
   * The ResizeObserver watches the PAPER, because its natural height changes for
   * reasons React does not re-render for: an image finishing loading, a dynamic fill
   * landing, a block's markup being replaced.
   */
  const paperBoxRef = useRef<HTMLDivElement | null>(null);
  const paperInnerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const inner = paperInnerRef.current, box = paperBoxRef.current;
    if (!inner || !box) return;
    const sync = () => {
      const h = Math.ceil(inner.offsetHeight * zoom);
      const w = Math.ceil(inner.offsetWidth * zoom);
      if (h > 0 && box.style.height !== h + "px") box.style.height = h + "px";
      if (w > 0 && box.style.width !== w + "px") box.style.width = w + "px";
    };
    sync();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(sync);
    ro.observe(inner);
    return () => ro.disconnect();
    /* `editing`/`echPending` are declared below and switching branch swaps the
       ref'd node, which re-runs this effect anyway. */
  }, [zoom, blocks, device]);

  /* ── toast (monolith .estoast + toast()) ── */
  const [toastMsg, setToastMsg] = useState("");
  const [toastOn, setToastOn] = useState(false);
  const toastT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toast = (m: string) => {
    setToastMsg(m); setToastOn(true);
    if (toastT.current) clearTimeout(toastT.current);
    toastT.current = setTimeout(() => setToastOn(false), 1800);
  };

  /* ── the red guardrail flash (audit row 2, monolith `__grFlash` L12991–12998) ──
     Fires when a write was actually TRUNCATED and repeats the exact limit wording
     the panel counter uses — "Limit reached — 2 lines max" (Lauren, 2026-08-04),
     not a generic "too long". Lives here rather than in the Inspector because it
     is position:fixed over the whole Studio; the monolith appends it to `.es` for
     the same reason. */
  const [flashMsg, setFlashMsg] = useState("");
  const [flashOn, setFlashOn] = useState(false);
  const flashT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const grFlash = (m: string) => {
    setFlashMsg(m || "Limit reached"); setFlashOn(true);
    if (flashT.current) clearTimeout(flashT.current);
    flashT.current = setTimeout(() => setFlashOn(false), 2200);
  };

  /* ── SFMC: Lauren's blank document shell + Layne's ECH blocks (monolith __grSync
     + __echRegister) ────────────────────────────────────────────────────────────
     The response used to be read for two things only — the shell and a `grMap`
     keyed `sfmc_<id>` that was then looked up by a FULL_REGISTRY type like `hero`,
     a key space it can never intersect (audit rows 13 + 14). Now every ECH block
     REGISTERS as a real block whose type IS `sfmc_<id>`, so the guardrail lookup
     (`grFor`) is symmetric with the write and the palette can offer them.
     `echVer` exists only to re-render once registration lands. */
  /* ── THE SNAPSHOT IS ADOPTED BEFORE THE FIRST PAINT ─────────────────────────
     `/sfmc-blocks?op=list&deep=1` is 16–60s and nothing can be assembled without
     it, so on a warm browser the last good payload is registered SYNCHRONOUSLY in
     these initialisers — the Studio opens with Layne's blocks already on the canvas
     and the network refresh happens behind it (`echSnap.ts`). On a cold browser
     there is nothing to adopt and `libReady` stays false, which is what drives the
     honest loading state below. */
  /* one shell for the whole app — App renders the submit/preview document with the
     same hook, so the Studio export and the submitted HTML cannot diverge (2026-09-15) */
  /* per template since 2026-10-06 — a bespoke doc (echTplName "") falls back to the
     request's template App registered, so the export matches App's submit HTML */
  const shell = useEchShell(echTplName || undefined); /* adopts the snapshot in its initialiser — keep it above echVer/libReady */
  /* A TEMPLATE'S OWN SHELL STYLES THE CANVAS TOO (2026-10-06). The canvas draws blocks
     without the shell; a named (bare) shell carries the CSS its modules are built for,
     so its head rules are rescoped onto the paper — see services/echMobile.ts
     shellCanvasCss. The default shell is untouched: nothing is injected for it. */
  const shellBare = isBareShell(shell);
  useEffect(() => {
    const ID = "ech-shell-canvas";
    const css = shellBare && shell ? shellCanvasCss(shell, device === "mobile") : "";
    if (!css) { document.getElementById(ID)?.remove(); return; }
    let el = document.getElementById(ID) as HTMLStyleElement | null;
    if (!el) { el = document.createElement("style"); el.id = ID; document.head.appendChild(el); }
    if (el.textContent !== css) el.textContent = css;
    return () => { document.getElementById(ID)?.remove(); };
  }, [shell, shellBare, device]);
  /* SEEDED FROM THE MODULE, NOT FROM 0. The registry is module-scope and this effect
     is per-mount, so on a SECOND Studio mount the blocks are already registered at
     version N. Starting at 0 made the first render of that mount look like a version
     change and rebuilt the document twice per pull; seeded, a reopen with no fresher
     markup is a no-op and a genuinely newer pull rebuilds exactly once. */
  const [echVer, setEchVer] = useState(() => echVersion() + echTplVersion());
  /** blocks are usable right now — from the snapshot or from the network */
  const [libReady, setLibReady] = useState(() => echLibReady());
  useEffect(() => {
    /* ONE pull per session, shared with the Templates tab (`sfmcSource`). The
       PROCESSING is now byte-gated (`echAdopt`): registering ALL of Layne's blocks
       (guardrails or not — 11 of 28 have none) and loading the ECH_TPL_* definition
       blocks that ARE the template layer, but only when the payload actually
       changed. A change still bumps `echVersion() + echTplVersion()`, which is what
       drives `echRebuildKeepEdits` and carries the user's edits onto the fresher
       markup (audit row 20); identical markup is left completely alone. */
    sfmcBlocksOnce()
      .then((blocks) => {
        /* OBSERVE THE VERSIONS, DO NOT GATE ON BEING THE ADOPTER (measured live in
           Chrome, 2026-08-19). Two components read the SAME cached pull — this one
           and the Templates tab (`useEchTemplateList`) — and `echAdopt` returns true
           only to whichever `.then` runs first. Gating `setEchVer` on that return
           value meant the form usually adopted first, StudioView got `false`, never
           re-rendered, `echTpl` never re-derived, and the canvas kept the legacy
           starter six instead of Layne's twelve. Observed exactly that: snapshot
           written (419KB), blocks registered, canvas still "Logo Header / Hero Image
           / Headline / Body Copy / Button / Social Footer".
           Syncing unconditionally is also cheaper than it looks: the versions only
           move when the payload's bytes moved, and React bails out on an identical
           value — so this is still one rebuild per genuine change, and none for a
           pull that brought the same library. */
        echAdopt(blocks, "sfmc");
        setEchVer(echVersion() + echTplVersion());
        setLibReady(true);
      })
      /* A FAILED REFRESH KEEPS THE SNAPSHOT (echSnap rule 2) — `libReady` is set
         from what is actually in hand, never cleared to false. Cold + offline is
         the only case that stays "loading", and that is the truth. */
      .catch(() => { setLibReady(echLibReady()); });
  }, []);

  /* ── AND KEEPS FOLLOWING WHILE THE EDITOR IS OPEN (2026-09-12, Justin/Jack) ──────
     Same adopt path as the manual Refresh: the byte gate bumps the versions only when
     the payload moved and `echRebuildKeepEdits` carries every typed field onto the
     fresher markup, selection re-pointed. Identical markup → nothing, not even a render.
     TWO RULES SO IT NEVER GETS IN SOMEONE'S WAY (Jack: "will this interfere when someone
     is working on an existing block? … don't want that toast to show"):
       · SILENT — no toast. A block that quietly matches Content Builder is the expected
         state, not news; the manual Refresh keeps its toast because it was asked for.
       · NEVER MID-KEYSTROKE — the rebuild re-keys the Inspector, which would drop focus
         out of a field being typed in. So while a panel input has focus, or anything was
         typed in the last few seconds, the fresher payload is HELD and applied the moment
         the requester pauses. Nothing is lost either way; this only chooses the moment. */
  const heldBlocks = useRef<SfmcBlock[] | null>(null);
  const lastInput = useRef(0);
  useEffect(() => {
    const mark = () => { lastInput.current = Date.now(); };
    document.addEventListener("input", mark, true);
    document.addEventListener("keydown", mark, true);
    return () => { document.removeEventListener("input", mark, true); document.removeEventListener("keydown", mark, true); };
  }, []);
  const applyFresh = (blocks: SfmcBlock[]) => {
    if (!echAdopt(blocks, "sfmc")) return;
    setEchVer(echVersion() + echTplVersion());
    setLibReady(true);
  };
  const busyTyping = () => {
    const ae = document.activeElement as HTMLElement | null;
    const inPanel = !!ae && !!ae.closest(".esinsp") && /^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName);
    return inPanel || Date.now() - lastInput.current < WATCH_QUIET_MS;
  };
  useEffect(() => {
    const unsub = sfmcBlocksWatch((blocks) => {
      if (busyTyping()) { heldBlocks.current = blocks; return; }
      applyFresh(blocks);
    });
    /* a held payload lands on the first quiet moment */
    const t = window.setInterval(() => {
      if (heldBlocks.current && !busyTyping()) { const b = heldBlocks.current; heldBlocks.current = null; applyFresh(b); }
    }, 1000);
    return () => { unsub(); window.clearInterval(t); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── TEMPLATE ASSEMBLY (monolith loadTpl → echDoc — audit row 23) ─────────────
     THE BLOCKER THIS CLOSES. The form preselects a template and hands the Studio
     its preview HTML, so `editing` was true, TemplateEditor took the canvas, and
     the `.esmod` block canvas that the ECH palette, the ECH Inspector and the
     row-19 dynamic fill all live on never appeared (observed live 2026-08-19:
     `.esmod` count 0). Everything the previous pass built was correct and
     unreachable from Jack's own path.

     The monolith splits this two ways and so does this, because the two cases are
     not the same intent:
       • a NEW template (`loadTpl`, L9204) → a FRESH `echDoc()`. Nothing is carried:
         the user asked for a different email, and carrying their body copy into it
         would be inventing an edit they never made. Undo history is reset, which is
         `esResetHist()`.
       • the SAME template rebuilt on fresher markup (a `/sfmc-blocks` pull landing
         underneath the user) → `echRebuildKeepEdits()`, which carries every field
         forward on content id + occurrence index (audit row 20).
     WHICH BRANCH IS DECIDED BY THE DOCUMENT, NOT BY A MOUNT-SCOPED REF (code review
     2026-08-19 #4). It used to be a `useRef`, and that is why the carry branch was
     unreachable: the ref is null at mount, so `fresh` was always true and a spy
     measured ZERO calls to `echRebuildKeepEdits` on every path. Worse than dead
     code — the repo's second `/sfmc-blocks` pull lands on the SECOND Studio mount
     (the fetch effect is per-mount; the block registry is module-scope), so closing
     and reopening the Studio re-assembled from block defaults and silently threw
     away every ECH edit the user had made.
     `echAsmOf` reads the stamp off the blocks themselves, which is the only state
     that outlives a mount — the monolith gets this for free because `doc` and
     `curTpl` are page globals. Three outcomes now:
       same template + same markup → return, the document is left completely alone
       same template + NEWER markup → echRebuildKeepEdits, every field carried
       a different template        → fresh echDoc, nothing carried */
  const echTpl = useMemo(() => echTplFor(echTplName || ""), [echTplName, echVer]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!echTpl) return;
    const asm = echAsmOf(blocks);
    const fresh = !asm || asm.tpl !== echTpl;
    if (!fresh && asm!.ver === echVer) return;
    const doc = fresh ? echDoc(echTpl, makeBlock) : null;
    const r = fresh ? (doc && doc.length ? { doc, sel: null } : null)
      : echRebuildKeepEdits(echTpl, blocks, selId, makeBlock);
    if (!r) return;
    echStamp(r.doc, echTpl, echVer);
    setBlocks(() => r.doc);
    setSelId(r.sel);
    if (fresh && onResetHistory) onResetHistory();
  }, [echTpl, echVer]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── DYNAMIC FILL OVER THE WHOLE DOCUMENT (monolith __echFillAll — audit row 19) ──
     Fill used to happen only inside the open panel, only on an explicit button
     click, and only for the selected block, so the canvas sat on the blocks'
     shipped placeholders until each one was clicked. Jack, 2026-08-19: "logo still
     isn't auto pulling from the first tab… this should auto happen before I select
     an individual block."
     `echFillAll` returns one patch per block that CHANGED, so the pass converges:
     apply → re-run → nothing left to write → stop. A Manual block, a field at the
     wrong level, and a field the user has taken over are all skipped inside it. */
  const baseEchCtx = useEchDyn(step1);

  /* THE AMENITY ICON LIBRARY, off Layne's SFMC folder (2026-08-21). Mounted here
     because the Inspector's amenity picker lives under this view and reads the
     library synchronously while rendering — this is what makes the folder's 45
     icons available instead of the 4-icon fallback. Result is ignored on purpose:
     a failure costs breadth, not function (see the hook's header). */
  useAmenityIcons();

  /* ═══════════════════════════════════════════════════════════════════════════════
     THE WIZARD'S OWN PACKS — 2026-08-20, Jack: "i want the right panel to have the
     other view where i don't even need a community — i click the + button and can
     select the info myself."
     ═══════════════════════════════════════════════════════════════════════════════
     `useEchDyn` fetches exactly what STEP 1 names: the one picked community's pack,
     plus a masterplan's child collections. That made Step 1 a precondition for the
     Dynamic pane — no community there meant no pack, so `echDynVal` returned "" and a
     floorplan chosen in the wizard changed nothing on the canvas.
     The monolith has no such tie: `window.__lvPackCache` holds whatever the WIZARD
     loaded. This state is that cache, and it is filled from two places, which is the
     whole of what makes a pick durable:
       1. `onPacks` — the wizard hands over what it just fetched, on Done;
       2. the effect below — a pick already stored in the document whose pack is not
          in memory, which is every reopened job and every Studio remount.
     Deliberately NOT reset on a Step-1 change: a block's pick is the requester's
     choice about THAT block, and dropping the pack under it would blank the card the
     moment they edited an unrelated field on Step 1. */
  const [pickPacks, setPickPacks] = useState<Pack[]>([]);
  const [packsPending, setPacksPending] = useState(false);
  const addPickPacks = useCallback((ps: Pack[]) => {
    if (!ps || !ps.length) return;
    setPickPacks((cur) => {
      const seen: Record<string, 1> = {};
      cur.forEach((p) => { if (p.code) seen[p.code] = 1; });
      const add = ps.filter((p) => p && p.code && !seen[p.code]);
      /* SAME ARRAY WHEN NOTHING IS NEW — `echCtx` is a dependency of the fill effect,
         so returning a fresh array every time would re-run the whole document fill on
         every wizard Done that added nothing. */
      return add.length ? cur.concat(add) : cur;
    });
  }, []);

  const echCtx = useMemo<EchCtx>(() => (
    pickPacks.length || packsPending
      ? { ...baseEchCtx, pickPacks, pending: !!baseEchCtx.pending || packsPending }
      : baseEchCtx
  ), [baseEchCtx, pickPacks, packsPending]);

  /* ── A PICK OUTLIVES THE SESSION THAT MADE IT ────────────────────────────────────
     The pick is a community CODE on the block; the pack it resolves against is not
     saved with it. Reopen the job (or close and reopen the Studio, which remounts this
     view) and `pickPacks` is empty again, so the Featured card would fall back to the
     positional plan or blank. So: read every code the document's picks name, and fetch
     the ones not already in hand — through `feedCommunityPack`, the SAME page-level
     promise cache the wizard now goes through, so a code the wizard just fetched is a
     cache hit rather than a second request.
     `packsPending` rides into `ctx.pending` while that is in flight, which is what
     stops `echDynPatch` writing "" over a real value in the second before it lands. */
  const pickCodeKey = useMemo(() => echPickCodes(blocks as DynBlock[]).join(","), [blocks]);
  const haveCodes = useMemo(() => {
    const s: Record<string, 1> = {};
    for (const p of echPacks(baseEchCtx)) if (p.code) s[p.code] = 1;
    for (const p of pickPacks) if (p.code) s[p.code] = 1;
    return s;
  }, [baseEchCtx, pickPacks]);
  useEffect(() => {
    const want = pickCodeKey.split(",").filter(Boolean).filter((c) => !haveCodes[c]);
    if (!want.length) return;
    let live = true;
    setPacksPending(true);
    Promise.all(want.map((code) =>
      feedCommunityPack(code)
        .then((C): Pack | null => (C ? { code, name: C.community?.name || code, C } : null))
        .catch(() => null)))
      .then((rows) => { if (live) addPickPacks(rows.filter(Boolean) as Pack[]); })
      .finally(() => { if (live) setPacksPending(false); });
    return () => { live = false; };
    /* `haveCodes` is re-derived on every ctx change; the CODE LIST is what decides
       whether anything is missing, and the filter above re-reads the current value */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickCodeKey, haveCodes, addPickPacks]);

  /* ── THE FILL PASS LIVES IN `useEchDocFill` NOW (2026-09-14) ─────────────────────
     Moved verbatim so the FORM can run the same pass while the Studio is closed — the
     two views swap, and a document the Studio had touched used to freeze on Step 1 until
     the Studio was reopened (Jie's "photos popping up haphazardly": a removed
     community's plan card stayed on the rail). Same drop → clone → fill convergence. */
  /* the fill's writes are derived, never the requester's — they stay out of undo (#131) */
  const onPatchFill = useCallback((id: string, p: Record<string, unknown>) => onPatch(id, p, { history: false }), [onPatch]);
  useEchDocFill({ blocks, setBlocks, onPatch: onPatchFill, makeBlock, echCtx });

  /* ── edit-real-template mode — the HTML arrives from the form (sent email /
     community fill / favorite via App's onLoadStudioHtml); the Studio's own
     gallery + Blank canvas went with the palette in the MVP-only cutover ── */
  /* a loaded template → edit-real-template canvas; else block build.
     AN ASSEMBLED ECH TEMPLATE WINS (audit row 23): the monolith's `loadTpl` sets
     `externalTplHtml = null` before taking the ECH doc, so the flat preview HTML
     the form loaded is not the document any more — Layne's real blocks are. Only
     `echTpl` matters here, not `blocks`: if assembly is still in flight the canvas
     must already be in block mode or TemplateEditor mounts first and steals it. */
  /* ── THE HONEST LOADING STATE (2026-08-19) ────────────────────────────────────
     The wrong-view-then-swap bug, stated plainly: a template IS selected on the
     form, but until the block library lands `echTplFor()` cannot resolve it, so
     `editing` was true, `TemplateEditor` took the canvas with the flat preview
     HTML, and 16–60s later the assembled block canvas replaced it under the user.
     With no `tplHtml` the alternative was worse — the "Your email is empty" empty
     state, which is a lie about a template that is on its way.

     So while a template is requested and the library is not in hand, the canvas
     shows neither: it says what it is doing. This is only ever reachable on a COLD
     browser — a warm one adopted the snapshot in the initialiser above and
     `libReady` was already true on the first render. */
  const echPending = !!echTplName && !echTpl && !libReady;
  /* keyed on `libReady`: the caps come from the registry, so before the blocks land
     `capsByLabel()` is legitimately empty and must be recomputed once they arrive —
     otherwise a resend opened during the first load of the day is silently uncapped */
  const tplCaps = useMemo(() => (resend ? capsByLabel() : undefined), [resend, libReady]); // eslint-disable-line react-hooks/exhaustive-deps -- libReady is the point: capsByLabel reads the registry, which is empty until the blocks land
  const editing = !!tplHtml && !echTpl && !echPending;
  /* TEMPLATE SECTION inspector state (Jack 2026-08-09 studio audit: the monolith's
     template mode HAS a right-rail inspector — the port was missing the layer) */
  const [tplSel, setTplSel] = useState<TplSection | null>(null);
  const tplApi = useRef<TplEditorApi | null>(null);

  const sel = blocks.find((b) => b.id === selId) ?? null;
  /**
   * THE EXPORT CARRIES ITS OWN RECIPE (2026-08-20).
   *
   * `stampManifest` writes which blocks, in which order, with which values, into an
   * HTML comment before `</body>`. It is stamped HERE rather than inside
   * `renderDocument` because `email/document.ts` deliberately holds no dependency on
   * the ECH layer, and the recipe is an ECH concept.
   *
   * This is the export used by the submit path AND by
   * `saveFavorite`, which is the point: both are ways a build comes back later, and
   * a build that comes back without its recipe comes back as flat HTML that no
   * guardrail can govern. Stamping the value the preview also reads keeps the
   * previewed bytes and the copied bytes identical — the comment is inert in every
   * client, and `stampManifest` replaces its own previous stamp rather than
   * accumulating one per keystroke.
   */
  const exportHtml = useMemo(
    () => stampManifest(renderDocument(blocks, shell), buildManifest(blocks)),
    [blocks, shell],
  );
  /* the template selector's list and the Template tab's order, off the live library */
  const m2Templates = useMemo(() => (m2On ? echTemplateNames() : []), [m2On, echVer]); // eslint-disable-line react-hooks/exhaustive-deps
  const m2TplOrder = useMemo(() => {
    if (!m2On || !echTpl) return [] as string[];
    try { return (echSeq(echTpl) || []).map((x) => x.block); } catch { return [] as string[]; }
  }, [m2On, echTpl, echVer]); // eslint-disable-line react-hooks/exhaustive-deps
  /* every block on the canvas checked against its SFMC guardrails (monolith
     __grViolations). `grFor` reads the same key the registration wrote — `echVer`
     is in the deps because registration happens outside React state. */
  /* `patternRulesFor` is the THIRD argument for a reason (2026-09-01): `grFor` returns
     the cap-filtered map, which drops url/number/tel/select — where 68 of the 88
     declared patterns live. Passing only `grFor` here is what made the previous fix
     look complete while checking none of them. */
  /* ── ALT-TEXT GAPS RIDE IN THE SAME LIST (2026-09-02, Layne's Contact Us ruling) ──
     A data-source Contact Us block renders every headshot with whatever alt it has,
     empty included, and the missing ones are COUNTED here — "3 headshots are missing
     alt text" — instead of holding the image back. `kind: "alt"` so nothing reads it
     as a character cap; it never blocks sending. */
  /* one call, both halves: `gateMode: "warn"` on the source routes the SAME sentence
     to the warnings list instead of the Done gate (Layne's first-run ask, 2026-09-09) */
  const dataGate = useMemo(() => dataGateFindings(blocks, echCtx),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blocks, echVer, echCtx]);
  const viols = useMemo<(Violation & { blockId?: string })[]>(() => [
    ...blocks.flatMap((b) => violations(grFor(b.type), b.data, patternRulesFor(b.type)).map((v) => ({ ...v, blockId: b.id }))),
    ...echContactWarnings(blocks as unknown as DynBlock[], echCtx).map((w) => ({ ...w, kind: "alt" as const })),
    /* a CTA link the label left behind, or a label the Hub has no page for (#268) */
    ...echCtaLinkWarnings(blocks as unknown as DynBlock[]).map((w) => ({ ...w, kind: "data" as const })),
    /* a data fault the source asked to WARN about rather than block — same sentence,
       non-blocking channel (2026-09-09) */
    ...dataGate.warning.map((r) => ({ module: r.type, field: r.k, label: r.block, message: r.field, kind: "data" as const })),
  ], [blocks, echVer, echCtx, dataGate]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── THE REQUIRED-BLANK GATE (Jack 2026-08-21; mockup REQUIRED-GATE-MOCKUP-
     20260821.html, locked to solid cherry). Done becomes the count while a
     required field is blank — a button URL left on the shipped bare "https://"
     or a photo still on Layne's placeholder art. Free navigation everywhere;
     the gate lives ONLY on Done (and the form's submit belt). Listing-table
     rows are exempt: blanks there are dropped by the renderer / auto-resolve
     from inventory, so nothing blank can ship from them. */
  /* `feedPending` — an unclaimed field the feed is about to write is not counted while
     the feed is in flight (2026-09-10, the "flashes then vanishes" gate). Once the feed
     settles the count is exactly what it always was. */
  const feedPending = !!echCtx.pending;
  /* ── THE DATA GATE RIDES IN THE SAME LIST (2026-09-09, Layne's no-row ask) ──────
     A division with no row in a block's data extension, an empty mandatory column, or
     a fed value that fails its pattern, as `RequiredBlank` rows with `kind: "data"`.
     Merged here rather than given its own surface for the standing reason
     `requiredBlanks` has one implementation and three callers: the count, the popover,
     the cherry ring and the jump-to all work unchanged, and the Studio, the submit belt
     and the panel cannot disagree about what is missing. `echCtx` is in the deps
     because the rows arrive after first render. */
  /* ── A BROKEN GUARDRAIL IS A REQUIRED ROW, NOT A SECOND CARD (2026-09-12) ──────
     Jack, on two screenshots: the "⚠ 1 required missing →" popover top-right and, at
     the bottom of the canvas, a separate "Fix before sending — SFMC guardrails" card
     saying the hero is still the placeholder — "no, should pop up here" (the popover)
     "… what is the bottom one doing?" Same gate, two surfaces. A hard violation — a cap
     or a format the requester can act on — now rides the popover as one more row under
     its block, with the same Fix → jump and the same field highlight, and the card is
     gone. Fed-value format warnings stay where they already were: the Inspector's line
     under the field. */
  const hardViols = useMemo(() => viols.filter((v) => (v.kind === "cap" || v.kind === "pattern") && !v.fed), [viols]);
  const hardRows = useMemo<RequiredBlank[]>(() => hardViols.map((v) => {
    const b = blocks.find((x) => x.id === v.blockId);
    const gr = b ? grFor(b.type) : undefined;
    return {
      id: String(v.blockId || ""), type: b ? b.type : "", block: String((gr && gr.label) || v.module || "Block"),
      k: v.field,
      /* the whole sentence, as the card used to show it; `kind: "data"` keeps the
         popover from folding the label's own parenthetical */
      field: v.label + " · " + v.message,
      kind: "data",
    };
  }), [hardViols, blocks]);
  const reqBlanks = useMemo(
    () => [...requiredBlanks(blocks, { feedPending }), ...dataGate.blocking, ...hardRows],
    /* `echVer` looks unused to the linter and is not: both producers read the block
       REGISTRY, which is populated outside React state, so a re-registration has to
       re-derive this list. Same reason the single-line version carried the disable. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blocks, echVer, feedPending, dataGate, hardRows],
  );
  /* ── DONE WAITS RATHER THAN PASSES WHILE A GATING SOURCE IS UNANSWERED ──────────
     Layne, 2026-09-09, on the hole in the design: silence while the lookup is pending
     is safe against a false BLOCK and is a false PASS on Done — click it in the first
     second and the gate does not exist yet, so you sail through and ship the dead link.
     Scoped to sources the canvas needs (not `ctx.pending`, which would hold Done on a
     fresh Studio for unrelated feeds), and `DE_FAILED` releases clean, because an error
     is infrastructure and this gate never decides anything on infrastructure. */
  /* ── NO DIVISION, NOTHING TO CHECK (2026-09-12) ─────────────────────────────────
     Jack on emailbuilder-qa: Design Email opened from a blank Step 1 (no division yet)
     and Done read "Checking division data…" for good. The row fetch is keyed by the
     division, so with none picked it never runs, `deRows` stays null and the gate
     waited forever for an answer nothing was going to give. With no division there is
     no division row to check; the form's own required gate catches the missing
     division. The "no ctx → pending" rule stays for the registry-not-loaded window. */
  const dataPending = useMemo(() => !!String(echCtx.divisionCode || "").trim() && dataGatePending(blocks, echCtx), [blocks, echVer, echCtx]); // eslint-disable-line react-hooks/exhaustive-deps
  /* the pinned blocks, for the drag handle. `echVer` is in the deps because the rules
     arrive with the SFMC library — before it lands nothing is pinned, and the moment it
     does every handle re-answers (2026-08-27). */
  /* ── M2 PHASE 2: THE OPEN TEMPLATE'S OWN RULES (Layne, 2026-10-07) ─────────────────
     A template that declares `palette` or `editableBetween` turns the guardrails back ON
     for the panel, plus its play area. A template that declares neither stays phase 1
     (every block anywhere) — so nothing changes until Layne declares the keys on the
     `_TEST` set. See services/m2Rules.ts. */
  const m2r = useMemo<M2TplRules | null>(
    () => (m2On && echTpl ? m2TplRules(ECH_TPLDEF[echTpl]) : null),
    [m2On, echTpl, echVer], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const m2Live = !!(m2r && m2r.active);
  const free = m2On && !m2Live;
  const m2NameOf = (t: string) => echNameOf(t) || "";
  const m2GrOf = (t: string) => {
    const m = echMeta(t);
    return ((m && (m.full || m.gr)) || null) as unknown as Record<string, unknown> | null;
  };
  /** after a user's add / move / delete in phase 2: orphan headers out, spacers re-applied */
  const m2Tidy = (doc: AnyBlockInstance[]): AnyBlockInstance[] => {
    if (!m2Live || !m2r) return doc;
    const libHeads = echBlocksMeta()
      .map((m) => (m.full as unknown as { addsWith?: { block?: string } } | null)?.addsWith?.block || "")
      .filter(Boolean);
    const noOrphans = m2DropOrphanHeads(doc, m2NameOf, m2GrOf, m2r, libHeads);
    return m2Respace(noOrphans, m2NameOf, m2GrOf, m2r, (name) => {
      const meta = echByName(name);
      return meta ? (makeBlock(meta.key) as AnyBlockInstance) : null;
    });
  };
  /* ── THE APP-MANAGED SPACER HAS NO CONTROLS (Layne, 2026-10-07: "the 50px spacer being
     app-managed is fine") ───────────────────────────────────────────────────────────
     Every block's `spacingAbove.block` is placed and removed by `m2Respace` after each
     add, move or delete. Its arrows and ✕ used to stay live, so a delete toasted
     "Section Spacer (50px white) removed" and the tidy put it straight back (measured on
     Coming Soon). Phase 2 only: the controls are off with the play-area sentence, and the
     drag handle with them. */
  const m2ManagedIds = useMemo(() => {
    const s = new Set<string>();
    if (!m2Live) return s;
    const managed = new Set(
      echBlocksMeta()
        .map((m) => {
          const sa = (m.full as unknown as { spacingAbove?: unknown } | null)?.spacingAbove;
          return typeof sa === "string" ? sa : String((sa as { block?: string } | undefined)?.block || "");
        })
        .filter(Boolean)
        .map((n) => n.toLowerCase()),
    );
    for (const b of blocks) if (managed.has(m2NameOf(b.type).toLowerCase())) s.add(b.id);
    return s;
  }, [blocks, echVer, m2Live]); // eslint-disable-line react-hooks/exhaustive-deps
  const pinnedIds = useMemo(() => {
    if (free) return new Set<string>();
    const s = lockedIds(blocks, echTplName);
    if (m2Live && m2r) m2LockedIds(blocks, m2NameOf, m2r).forEach((id) => s.add(id));
    m2ManagedIds.forEach((id) => s.add(id));
    return s;
  }, [blocks, echTplName, echVer, free, m2Live, m2r, m2ManagedIds]); // eslint-disable-line react-hooks/exhaustive-deps
  /* the M2 palette re-derives when the library lands or moves (`echVer`). Phase 2: only the
     template's `palette` chips, each greyed with the cap / "never both" reason when the
     email can't take another one right now (Layne, 2026-10-07). */
  const palGroups = useMemo(() => {
    if (!m2On) return [];
    /* Katy (2026-10-16): no button-only chip is ever offered, and a module the email already
       holds greys with the reason — on every template, guardrails off included */
    const docNames = blocks.map((b) => m2NameOf(b.type));
    const once = (it: { name: string; label: string; members?: string[] }) =>
      (it.members || [it.name]).map((n) => m2OnceViolation(docNames, n, it.label)).find(Boolean) || null;
    const g = paletteGroups(echBlocksMeta(), palQ)
      .map((grp) => ({
        ...grp,
        items: grp.items
          .filter((it) => !(it.members || [it.name]).every((n) => m2IsButtonBlock(n)))
          .map((it) => { const why = once(it); return why ? { ...it, off: why } : it; }),
      }))
      .filter((grp) => grp.items.length > 0);
    if (!m2Live || !m2r) return g;
    return g
      .map((grp) => ({
        ...grp,
        items: grp.items
          .filter((it) => m2PaletteOffers(m2r, it.members || [it.name]))
          .map((it) => {
            const why = it.off || capViolation(blocks as unknown as CapBlock[], it.type, echTplName)
              /* the play area's module cap (Layne, 2026-10-15) — a chip whose group already
                 has a run stays live: its block joins that run and the count does not move */
              || m2PanelFull(blocks, m2NameOf, m2r, it.members || [it.name]);
            return why ? { ...it, off: why } : it;
          }),
      }))
      .filter((grp) => grp.items.length > 0);
  }, [m2On, palQ, echVer, m2Live, m2r, blocks, echTplName]); // eslint-disable-line react-hooks/exhaustive-deps
  const [reqOpen, setReqOpen] = useState(false);
  /* ── ARMED BY THE DONE CLICK (Jack, same day: "should only pop up along with
     highlighting the red when they try and click done — otherwise it's just
     noise when building"). While building, the button is plain gold Done and
     nothing on the canvas is cherry. The first Done click WITH blanks arms the
     gate: cherry count, popover, rings, field flags. Fixing everything disarms
     it and Done comes straight back. */
  const [reqArmed, setReqArmed] = useState(false);
  /* the guardrail card that this comment used to describe is gone (2026-09-12) — a hard
     violation is a required-popover row now; see `hardRows` above */
  useEffect(() => { if (!reqBlanks.length && reqArmed) { setReqArmed(false); setReqOpen(false); } }, [reqBlanks.length, reqArmed]);
  /* ── DONE WAITS FOR THE FEED, THEN DECIDES ONCE (2026-09-10) ──────────────────────
     A Done click while the feed is still filling used to arm the gate on fields that
     were about to be written, then disarm a second later when they were. Now the click
     is held until `echCtx.pending` clears (a beat, not a wait — the pack and /refdata
     are already in flight), and then: blanks → arm and open, as before; none → close.
     Nothing is skipped: whatever the feed did NOT fill still gates. */
  const [doneWaiting, setDoneWaiting] = useState(false);
  useEffect(() => {
    if (!doneWaiting || feedPending) return;
    setDoneWaiting(false);
    if (reqBlanks.length > 0) { setReqArmed(true); setReqOpen(true); return; }
    onClose();
  }, [doneWaiting, feedPending, reqBlanks.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const reqIds = useMemo(() => new Set(reqArmed ? reqBlanks.map((r) => r.id) : []), [reqBlanks, reqArmed]);
  const jumpToReq = (id: string) => {
    setReqOpen(false); setSelId(id); setInspOpen(true);
    const el = canvasRef.current?.querySelector(`.esmod[data-id="${id}"]`) as HTMLElement | null;
    /* jsdom has no scrollIntoView — the selection/panel-open above is the behavior */
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  /* ══ INLINE CANVAS EDITING — double-click the text ON the email ═════════════
     Monolith `startInlineEdit` / `esInlineOn` (L12458–12509) plus the dblclick
     wiring at L12364. Jack, on the running build: "not able to edit anything else
     in the actual email — should be able to double click as well."

     THE ONE STRUCTURAL DIFFERENCE FROM THE MONOLITH, AND WHY.
     The monolith writes `m.data[k]` on every keystroke and repaints the block on
     BLUR (`esLive`). Here the block's markup is `dangerouslySetInnerHTML`, so a
     patch mid-keystroke re-renders, React resets `.mi`'s innerHTML wholesale, and
     the contenteditable node the user is typing into is destroyed under them —
     the "an edit in progress must not be blown away by a re-render" hazard. So:
       • while editing, NOTHING is patched. The DOM node holds the text.
       • the guardrail is still enforced per keystroke, against the DOM (below) —
         the cap cannot be out-typed, it just isn't written through yet.
       • Enter or blur commits ONCE: one `onPatch`, one re-render, one re-tag.
       • Escape puts the original text back and patches nothing.
     `blocksRef`/`patchRef` exist because the listeners are native (the node is not
     React's) and would otherwise close over the props of the render that started
     the edit. */
  const [inlineId, setInlineId] = useState<string | null>(null);
  /* re-runs the canvas pass once the card-photo crop probe answers (2026-09-25) */
  const [, setCropTick] = useState(0);
  const inlineRef = useRef<InlineEdit | null>(null);
  const blocksRef = useRef(blocks); blocksRef.current = blocks;
  const patchRef = useRef(onPatch); patchRef.current = onPatch;
  /**
   * THE EDITED BLOCK'S MARKUP IS FROZEN FOR THE DURATION OF THE EDIT.
   *
   * Observed live, 2026-08-19, before this existed: double-click the Body Copy
   * headline and the node went contenteditable and took focus (good) — and on the
   * very next render `.mi`'s innerHTML was rewritten, the contenteditable node
   * was destroyed, focus fell back to <body>, every `[data-ek]` binding on the
   * whole canvas went to 0, and `inlineId` stayed set so nothing re-tagged. One
   * double-click disabled inline editing until the Studio was reopened.
   *
   * `dangerouslySetInnerHTML` only rewrites when the STRING changes, so pinning
   * the string the node was created from means React cannot touch it however many
   * times the Studio re-renders underneath (a dynamic fill landing, the panel
   * opening, a toast). The freeze is dropped on commit/cancel, and the render that
   * follows writes the real markup once.
   */
  const frozenRef = useRef<{ id: string; html: string } | null>(null);
  /**
   * ── REACT 19 RE-APPLIES `dangerouslySetInnerHTML` ON OBJECT IDENTITY ─────────
   * Not on string equality — React 18's `diffProperties` compared `lastHtml !==
   * nextHtml`, React 19's `updateProperties` re-runs `setInnerHTML` whenever the
   * PROP OBJECT differs, and `{{ __html: blockHtml(b) }}` is a fresh object every
   * render. Measured on this build (react 19.1.0): a plain single click on a block
   * re-parsed all eleven blocks' email HTML and wiped every `[data-ek]` binding —
   * and, once inline editing existed, destroyed the contenteditable node
   * mid-edit and dropped focus back to <body> (observed live 2026-08-19, then
   * reproduced in jsdom: `AFTER PLAIN CLICK connected=false`).
   *
   * Caching the OBJECT per block, keyed on the html string, restores React 18's
   * behaviour: unchanged markup → same object → React does not touch the node.
   * This is worth having on its own — it stops the canvas re-parsing every
   * block's email on every zoom, select, hover-state and toast.
   */
  const htmlObjRef = useRef<Record<string, { __html: string }>>({});
  const htmlOf = (b: AnyBlockInstance): { __html: string } => {
    let html = (frozenRef.current && frozenRef.current.id === b.id) ? frozenRef.current.html : blockHtml(b);
    /* the footer shows the disclaimer as it is being built (Jack, 2026-10-19) */
    if (isDisclaimerBlock(b.type) && (dsParas.length || dsOverride(b))) html = footerWithDisclaimer(html, dsParas, dsOverride(b));
    const cur = htmlObjRef.current[b.id];
    if (cur && cur.__html === html) return cur;
    const obj = { __html: html };
    htmlObjRef.current[b.id] = obj;
    return obj;
  };

  /**
   * ONE LINE OF EDITOR CHROME WHEN AN AMENITY BLOCK RENDERS NOTHING.
   *
   * `amenTrimHtml` removes an empty icon cell and then any row left with nothing, so a
   * community with fewer than AMEN_MIN mappable amenities collapses the whole block to
   * zero pixels — correct behaviour that looks exactly like a deleted block (Layne,
   * 2026-08-28: "the amenities block disappeared lol").
   *
   * Drawn HERE, in the `.esmod` wrapper beside `.mtag`/`.mbar`, and NOT in the block's
   * markup — the canvas and the export share the same `render()`, so a note in the
   * markup would be pasted into Content Builder and mailed (the trap documented on
   * `IS_PLACEHOLDER` in email/document.ts). `renderRows` never walks this DOM.
   *
   * The library version is read so the note re-answers the moment /amenity-icons lands —
   * before it does, every community looks like it maps to nothing.
   */
  const amenNoteOf = (b: AnyBlockInstance): string | null => {
    try {
      const pairs = amenPairs(echMeta(b.type)?.fields);
      if (!pairs.length) return null;                       /* not an amenity block */
      void amenFolderVersion();
      const picked = amenPicked(b.data as Record<string, unknown>, pairs).length;
      return amenEmptyNote(picked, echCtx.picks, echAmenLib());
    } catch { return null; }                                /* a note is never worth a crash */
  };

  /** detach and either write the edit through or throw it away */
  const finishInline = (commit: boolean) => {
    const st = inlineRef.current;
    if (!st) return;
    inlineRef.current = null;
    const { el, t } = st;
    el.removeEventListener("input", st.onInput);
    el.removeEventListener("keydown", st.onKey);
    el.removeEventListener("blur", st.onBlur);
    el.removeAttribute("contenteditable");
    el.removeAttribute("spellcheck");
    try { el.blur(); } catch { /* noop */ }
    const b = blocksRef.current.find((x) => x.id === st.id);
    frozenRef.current = null;   /* before the state change, so the next render paints real markup */
    setInlineId(null);
    /* ESCAPE, or the block went away underneath the edit → restore, write nothing */
    if (!commit || !b) { el.textContent = st.orig; return; }
    const typed = readText(el);
    const data = b.data as Record<string, unknown>;
    const c = inlineCommit(b.type, data, t, typed);
    /* show what was actually kept, so a clamped commit doesn't leave the canvas
       displaying characters that were never stored */
    const shown = t.line >= 0 ? (c.value.split("\n")[t.line] ?? "") : c.value;
    if (shown !== typed) el.textContent = shown;
    if (c.clamped) grFlash(limitMessage(grMeta(b.type, t.k)));
    if (c.value !== String(data[t.k] ?? "")) {
      /* ── THE CANVAS EDIT CLAIMS ITS FIELD (2026-08-22) ─────────────────────────
         On a fed block (the listing table, the Featured cards) a bare write would
         be reverted by the very next fill — you would type a price on the email and
         watch it change back. `echClaimPatch` adds the key to `__socOff`, the same
         per-field takeover the panel's edits use, so the value is the requester's
         until they clear it. Non-fed blocks pass straight through. */
      /* a collection heading is written by the fill but sits outside its fed map, so
         `echClaimPatch` cannot claim it — claimed here, the same way the panel's own
         heading edit does, or the next fill would put the old name back (2026-09-25) */
      let p = echClaimPatch(b as unknown as DynBlock, c.patch);
      if (/^collection\d+Name$/.test(t.k) && !socIsOff(b as unknown as DynBlock, t.k)) {
        p = { ...p, __socOff: socOffList(b as unknown as DynBlock, t.k, true) };
      }
      patchRef.current(b.id, p);
    }
  };

  const beginInline = (id: string, el: HTMLElement, t: InlineTarget) => {
    const onInput = () => {
      const st = inlineRef.current;
      if (!st) return;
      const b = blocksRef.current.find((x) => x.id === st.id);
      if (!b) return;
      const cur = readText(st.el);
      /* deleting ALL the copy makes the browser discard the editable node and the
         next keystroke lands outside the block (Lauren, 2026-08-04) — a ZWSP keeps
         it alive and is stripped from everything we read or store */
      if (!cur.length && st.el.textContent !== ZW) { st.el.textContent = ZW; caretEnd(st.el); return; }
      const d = inlineDisplay(b.type, b.data as Record<string, unknown>, st.t, cur);
      if (!d.clamped) return;
      const pos = caretOffset(st.el);
      st.el.textContent = d.text;
      if (pos == null) caretEnd(st.el); else caretTo(st.el, Math.min(Math.max(0, pos - 1), d.text.length));
      grFlash(limitMessage(grMeta(b.type, st.t.k)));
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.stopPropagation(); finishInline(true); }
      /* stopPropagation matters: the Studio's window-level Escape closes the whole
         overlay, and cancelling an edit must not do that */
      else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); finishInline(false); }
    };
    const onBlur = () => finishInline(true);
    inlineRef.current = { id, t, el, orig: el.textContent ?? "", onInput, onKey, onBlur };
    setSelId(id); setInspOpen(true); setInlineId(id);
    el.setAttribute("contenteditable", "true");
    /* NATIVE SQUIGGLES ON, for the duration of the edit only (2026-08-22). This was
       "false", inherited from the monolith, so the one place copy is actually typed
       had no spell check at all. `finishInline` removes the attribute again, so it
       never reaches the exported document. */
    el.setAttribute("spellcheck", "true");
    el.addEventListener("input", onInput);
    el.addEventListener("keydown", onKey);
    el.addEventListener("blur", onBlur);
    try { el.focus(); } catch { /* noop */ }
    caretEnd(el);
  };

  /** monolith `startInlineEdit` (L12501) — resolve the double-clicked node to a field */
  const startInline = (b: AnyBlockInstance, ev: React.MouseEvent) => {
    const t = ev.target as HTMLElement | null;
    if (!t || !t.closest) return;
    /* the block's action chrome is not the email (monolith: `e.target.closest("[data-act]")`) */
    if (t.closest(".mbar") || t.closest(".mtag")) return;
    const mi = t.closest(".mi") as HTMLElement | null;
    if (!mi) return;
    /* AN IMAGE IS NOT TEXT — it is the Bynder picker's job, in the panel */
    if (t.closest("img")) return;
    if (inlineLocked(b.type)) { toast(blockLabel(b.type) + " can't be edited here — see the panel for why."); return; }
    if (inlineRef.current) return;
    const data = b.data as Record<string, unknown>;
    /* the PRE-BOUND path wins before any text matching (monolith L12502) */
    const ek = t.closest("[" + EK_ATTR + "]");
    let target = ekTarget(ek, data);
    let host = ek as HTMLElement | null;
    if (!target) {
      const el = (t.closest("td,p,span,a,div,h1,h2,h3,h4,b,strong,em,i") || t) as HTMLElement;
      if (!mi.contains(el)) return;
      target = resolveBind(inlineTargets(b.type, data), readText(el));
      host = el;
    }
    /* no binding → the single click has already selected the block and opened the
       panel, which is the monolith's `esSelect(m.id)` fallback */
    if (!target || !host) return;
    frozenRef.current = { id: b.id, html: htmlOf(b).__html };
    beginInline(b.id, host, target);
  };

  /**
   * Bind every rendered text node back to the field that produced it.
   *
   * NO DEPENDENCY ARRAY, ON PURPOSE. This has to be re-applied every time React
   * rewrites `.mi`'s innerHTML, and the set of things that can cause that is not
   * knowable from here — a dynamic fill landing, a swap, a template rebuild, a
   * remount with an identical `blocks` reference. A dependency list was tried and
   * a reopened Studio came back with ZERO bindings (observed live 2026-08-19),
   * because the mount's own effect pass and the markup React wrote did not line
   * up. Correctness beats the micro-optimisation; the `data-ektag` stamp below
   * makes the common case an attribute compare rather than a DOM walk.
   *
   * The one setState below cannot chain: it nulls `inlineRef` on the same pass,
   * so the branch that calls it is unreachable on the re-render it causes.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (editing) return;
    const host = canvasRef.current;
    if (!host) return;
    /* SELF-HEAL: an edit whose node is no longer in the document is over,
       whatever ended it. Without this the canvas stays un-taggable forever. */
    const live = inlineRef.current;
    if (live && !live.el.isConnected) {
      inlineRef.current = null;
      frozenRef.current = null;
      setInlineId(null);
      return;
    }
    if (inlineRef.current) return;   /* re-tagging would rebuild the node under the caret */
    host.querySelectorAll(".esmod").forEach((w) => {
      const id = w.getAttribute("data-id");
      const b = id ? blocksRef.current.find((x) => x.id === id) : null;
      const mi = w.querySelector(".mi") as HTMLElement | null;
      if (!b || !mi) return;
      try {
        const targets = inlineTargets(b.type, b.data as Record<string, unknown>);
        const sig = JSON.stringify(targets);
        const stamp = mi.getAttribute("data-ektag") || "";
        const bound = mi.querySelectorAll("[" + EK_ATTR + "]").length;
        /* already tagged for exactly this data, and the spans are still there */
        if (stamp === sig + "#" + bound) return;
        mi.setAttribute("data-ektag", sig + "#" + tagEditable(mi, targets));
      } catch { /* a malformed block must never take the canvas down */ }
      /* ── A LENNAR.COM CARD PHOTO PREVIEWS AT THE FRAME IT WILL SHIP AT (2026-09-25) ─────
         The submit crops it to the card's frame (cropCardPhotos); the canvas shows the
         same centred crop — but only once the proxy has proved it can read the pixels,
         so the preview never promises a crop the submit can't make. */
      try {
        if (/^ECH_Featured/.test(echNameOf(b.type) || "")) {
          const fr = cardFrame(b.type);
          mi.querySelectorAll("img").forEach((im) => {
            const src = im.getAttribute("src") || "";
            if (!isCdnPhoto(src)) return;
            if (!cdnCropReady()) { probeCdnCrop(src).then((ok) => { if (ok) setCropTick((n) => n + 1); }); return; }
            const el = im as HTMLImageElement;
            if (el.style.objectFit !== "cover") el.style.objectFit = "cover";
            if (el.style.height !== fr.h + "px") el.style.height = fr.h + "px";
          });
        }
      } catch { /* never take the canvas down */ }
    });
  });

  /* unmounting mid-edit must not leave listeners on a detached node */
  useEffect(() => () => {
    const st = inlineRef.current;
    if (!st) return;
    st.el.removeEventListener("input", st.onInput);
    st.el.removeEventListener("keydown", st.onKey);
    st.el.removeEventListener("blur", st.onBlur);
    inlineRef.current = null;
  }, []);

  /* ── block ops ── */
  /* ── ↑↓ ASKS THE SAME PLACEMENT ENGINE THE DRAG DOES (2026-08-27) ────────────
     Layne, `GUARDRAIL-KEYS-TO-READ-20260827.md`: "Logo, Contact Us, Social and
     Disclaimer can be moved" and "CTA row can be moved off the copy block." A swap was
     the whole implementation, so every declared placement rule was unenforced on this
     path as well as on the drag. `reorder` refuses a pinned block, keeps a copy+CTA pair
     together, and hands back Layne's own sentence to show. */
  const move = (id: string, d: -1 | 1) =>
    setBlocks((s) => {
      const i = s.findIndex((b) => b.id === id);
      if (i < 0) return s;
      /* a swap in index terms is an INSERTION at i-1 (up) or i+1 (down): the moved
         block leaves first, so down needs no +1 of its own */
      const to = d < 0 ? i - 1 : i + 1;
      if (to < 0 || to > s.length - 1) return s;
      if (free) return freeMove(s, id, to);
      if (m2Live && m2r && !m2CanMove(s, m2NameOf, m2r, id, to)) { toast(M2_LOCKED_MSG); return s; }
      const r = reorder(s, id, to, echTplName, { step: d });
      if (r.message) { toast(r.message); return s; }
      /* a header run moves as one (echPlacement.headerRun), so the play area is re-checked on
         the RESULT: the clicked block's own index can pass while the run lands outside it */
      if (m2Live && m2r && !m2SameLocked(s, r.doc as AnyBlockInstance[], m2NameOf, m2r)) { toast(M2_LOCKED_MSG); return s; }
      /* a move that splits a counted run can push the play area past its module cap */
      const fullWhy = m2Live && m2r ? m2ModulesBlocked(s, r.doc as AnyBlockInstance[], m2NameOf, m2r) : null;
      if (fullWhy) { toast(fullWhy); return s; }
      return m2Tidy(r.doc as AnyBlockInstance[]);
    });
  /* ── ONE PER EMAIL IS ENFORCED HERE, NOT IN THE BLOCK (2026-08-25, Layne: "not allowed to
     duplicate the hero image, the table header, the logo, etc… that needs to be something set
     in the dashboard itself"). A Content Builder block cannot refuse to be placed twice; the
     placing surface is the only thing that can. There are two of those — this ⧉ handler and
     `insertAt` below — and both ask `singletonBlocks`. The list is the same one `visualSpec`
     has always used to tick "Extra Blocks?", now shared rather than copied, so a duplicate is
     PREVENTED instead of merely reported on Katie's row after the fact.
     The refusal says why: a control that silently does nothing reads as a broken button. ── */
  const dup = (id: string) =>
    setBlocks((s) => {
      const i = s.findIndex((b) => b.id === id);
      if (i < 0) return s;
      const why = (m2On ? m2OnceViolation(s.map((b) => m2NameOf(b.type)), m2NameOf(s[i].type), blockLabel(s[i].type)) : null)
        || (free ? null : capViolation(s, s[i].type, echTplName));
      if (why) { toast(why); return s; }
      const nb: AnyBlockInstance = { id: newId(), type: s[i].type, data: JSON.parse(JSON.stringify(s[i].data)) };
      const out = s.slice(); out.splice(i + 1, 0, nb);
      const fullWhy = m2Live && m2r ? m2ModulesBlocked(s, out, m2NameOf, m2r) : null;
      if (fullWhy) { toast(fullWhy); return s; }
      toast(blockLabel(s[i].type) + " duplicated");
      return out;
    });
  /* ── THE DOCUMENT AS OF THE LAST WRITE IN THIS HANDLER, NOT THE LAST RENDER (2026-09-15)
     Driven on dev: Plan cards → Listing table → Plan cards brought back ONE card of
     three. `layToCards` hands the swap plus two inserts to run in one click; `insertAt`
     checked each insert's cap and placement against `blocks` — the PRE-swap render, in
     which the section is still the table — so "a Featured card directly under the
     table" failed the template's placement rule and both siblings were refused. The
     stash was intact; the checks were looking at the wrong document. `docRef` follows
     every write made here inside the same handler, and re-syncs to the rendered doc on
     every render, so a sequence of writes checks against what the previous write left. */
  const docRef = useRef<AnyBlockInstance[]>(blocks);
  docRef.current = blocks;
  const write = (updater: (s: AnyBlockInstance[]) => AnyBlockInstance[]) => {
    docRef.current = updater(docRef.current);
    setBlocks(updater);
  };
  const del = (id: string) => {
    /* the app-managed spacer (see m2ManagedIds) — every delete path, the inspector's too */
    if (m2ManagedIds.has(id)) { toast(M2_LOCKED_MSG); return; }
    const b = docRef.current.find((x) => x.id === id);
    /* the handler runs the real check too, so no path — the panel's "Delete this block",
       a card's ✕, a stale render — removes a pinned block (Layne, 2026-09-28) */
    const pinned = controlState(docRef.current as unknown as (PlaceBlock & CapBlock)[], id, echTplName).delete;
    if (pinned) { toast(pinned); return; }
    /* ── A DELETED CARD HANDS ITS LAYOUT STASH TO A SIBLING (2026-09-18) ────────────
       table → cards parks the typed Listing Table on the first card; delete that card and
       the section's only copy of the table went with it, so the next "Listing table" toggle
       built a fresh one (the other half of Laura Hughes' data loss, pilot call 09-18). The
       stash moves to the next card of the same type before the block goes. */
    const stash = b && (b.data as Record<string, unknown> | undefined)?.[LAY_STASH];
    const heir = stash && b ? docRef.current.find((x) => x.id !== id && x.type === b.type && !(x.data as Record<string, unknown> | undefined)?.[LAY_STASH]) : undefined;
    /* ── ✕ ON A FEATURED CARD REMOVES THAT CARD (2026-10-27, Layne: "clicking the X on any card
       deletes the last card, not the one I clicked") ── an unpicked card shows a plan by its
       position, so every card below moved up a plan and the email lost its LAST plan. The other
       cards are pinned to what they show, in this same write (echSection.echPinOnDelete). */
    const pins = echPinOnDelete(docRef.current as unknown as DynBlock[], id, echCtx);
    /* ── THE SECTION'S HEADER GOES WITH ITS LAST CARD, ON EVERY HOST (2026-10-30, Justin,
       SLC 1041310) ── see echPlacement.orphanedHeaders; m2Tidy only does this in test mode. */
    const afterDel = docRef.current.filter((x) => x.id !== id);
    const heads = new Set(orphanedHeaders(docRef.current as unknown as PlaceBlock[], afterDel as unknown as PlaceBlock[], echTplName));
    write((s) => m2Tidy(s.filter((x) => x.id !== id && !heads.has(x.id)).map((x) => {
      const y = heir && x.id === heir.id ? ({ ...x, data: { ...(x.data as Record<string, unknown>), [LAY_STASH]: stash } } as AnyBlockInstance) : x;
      const p = pins && pins[y.id];
      return p ? ({ ...y, data: { ...(y.data as Record<string, unknown>), ...p } } as AnyBlockInstance) : y;
    })));
    if (selId === id || (selId && heads.has(selId))) setSelId(null);
    if (b) toast(blockLabel(b.type) + " removed");
  };
  const patch = onPatch;
  /**
   * ── SWAP A BLOCK FOR ITS SFMC SIBLING, IN PLACE (audit row 16) ──────────────
   * "One or two buttons" is answered by two different Content Builder blocks, so
   * the panel swaps WHICH block sits here. The id is deliberately kept: the block
   * holds its position, stays selected, and — because `echRebuildKeepEdits` keys
   * on `ech:<contentId>#<occurrence>` rather than the instance id — a later SFMC
   * pull carries the NEW block's fields, which is the correct behaviour.
   */
  const swapBlock = (id: string, type: string, data: Record<string, unknown>) =>
    write((s) => s.map((b) => (b.id === id ? ({ id: b.id, type, data } as AnyBlockInstance) : b)));
  /* the same, for a whole Featured section in ONE write (2026-09-18) — ids kept, so
     the selection holds and the SFMC re-pull key (`ech:<contentId>#<occurrence>`)
     carries each card's new block exactly as the single swap does */
  const swapBlocks = (list: { id: string; type: string; data: Record<string, unknown> }[]) => {
    const m = new Map(list.map((x) => [x.id, x]));
    write((s) => s.map((b) => { const r = m.get(b.id); return r ? ({ id: b.id, type: r.type, data: r.data } as AnyBlockInstance) : b; }));
  };
  /* ── A WHOLE FEATURED SECTION REPLACED IN ONE WRITE (2026-09-25) ─────────────────────
     The picker's Done: the section's cards go and the picked set goes in at the first
     one's position, as ONE undo step. A card that keeps an existing id keeps its block
     (selection, SFMC re-pull key); a new card gets a fresh id. The new blocks are built
     OUTSIDE the updater, which `write` runs twice. */
  const replaceSection = (ids: string[], cards: { id: string; type: string; data: Record<string, unknown> }[]) => {
    if (!cards.length) return;
    const gone = new Set(ids);
    const ins = cards.map((c) => (c.id && gone.has(c.id)
      ? ({ id: c.id, type: c.type, data: c.data } as AnyBlockInstance)
      : ({ ...makeBlock(c.type), data: c.data } as AnyBlockInstance)));
    write((s) => {
      const at = s.findIndex((b) => gone.has(b.id));
      const out = s.filter((b) => !gone.has(b.id));
      out.splice(at < 0 ? out.length : at, 0, ...ins);
      return out;
    });
    if (selId && !ins.some((b) => b.id === selId) && gone.has(selId)) setSelId(ins[0].id);
  };
  const insertAt = (at: number, type: string, extra?: Record<string, unknown>) => {
    /* the second placement surface — see the note on `dup`. Reads the CURRENT doc rather than
       a flag, so it is still right after a template load, a duplicated job or an SFMC re-pull,
       none of which come through here and any of which can seed a doc that already holds one. */
    const cur = docRef.current;
    /* Katy's add-block rules (m2Blocks.m2OnceViolation, Teams 2026-10-16): once per email,
       no added buttons — held even with the guardrails off */
    const onceWhy = m2On ? m2OnceViolation(cur.map((b) => m2NameOf(b.type)), m2NameOf(type), blockLabel(type)) : null;
    if (onceWhy) { toast(onceWhy); return; }
    const capWhy = free ? null : capViolation(cur, type, echTplName);
    if (capWhy) { toast(capWhy); return; }
    /* ── AND WHERE it may go, not just how many (2026-08-27) ────────────────────
       The cap check answers "may this email hold another one"; the placement rules
       answer "may it go HERE". Nothing may land above the logo, inside the footer trio,
       between the copy block and its CTA row, or between the table header and its
       table — all declared, none of it enforced until now (Layne's report). Only a NEW
       problem blocks: a document that already breaks a rule stays editable. */
    /* ── M2 PHASE 2 (Layne, 2026-10-07): an add lands inside the play area, and a block whose
       guardrail says `addsWith` (Featured / Listing Table → the blue Table Header) brings that
       block in above it when the section has none — instead of the add being refused by the
       header's own placement rule. The placement check then runs on the doc WITH the header. */
    if (m2Live && m2r) at = m2ClampInsert(cur, m2NameOf, m2r, at);
    const headName = m2Live ? m2NeedsCompanion(cur, m2NameOf, m2GrOf, type, at) : null;
    const headMeta = headName ? echByName(headName) : null;
    const headBlock = headMeta ? (makeBlock(headMeta.key) as AnyBlockInstance) : null;
    const checkDoc = headBlock ? [...cur.slice(0, at), headBlock, ...cur.slice(at)] : cur;
    const checkAt = headBlock ? at + 1 : at;
    const whereWhy = free ? { ok: true as const, message: "" } : insertBlocked(checkDoc, type, checkAt, echTplName);
    if (!whereWhy.ok) { toast(whereWhy.message || "That block cannot go there."); return; }
    /* the play area's module cap (Layne, 2026-10-15): refused only when this add raises the
       count past it — a Featured card joining its run never does */
    if (m2Live && m2r) {
      const probe = [...checkDoc.slice(0, checkAt), { id: "__m2probe", type }, ...checkDoc.slice(checkAt)] as AnyBlockInstance[];
      const fullWhy = m2ModulesBlocked(cur, probe, m2NameOf, m2r);
      if (fullWhy) { toast(fullWhy); return; }
    }
    const nb = makeBlock(type);
    /* ── A LISTING BLOCK PLACED FROM THE MENU STARTS EMPTY (2026-08-20, Jack on the
       full-of-placeholders panel: "i dont see multiple collections as an option or
       even adding homesites"). The block's declared defaults fill all ten slots with
       "101 / Plan Name / $399,990" demo rows, so a fresh placement read "Full" with
       both plus cards disabled — the ADD experience he approved was unreachable
       without ✕-ing ten placeholder rows first. Homes start blank; templates,
       duplicates and dynamic fills set their own data and are untouched. The email
       side is already honest about this: empty rows are dropped at render. */
    const hk = Object.keys(nb.data || {}).filter((k) => rowOf(k));
    if (hk.length) {
      const d = { ...(nb.data as Record<string, unknown>) };
      hk.forEach((k) => { d[k] = ""; });
      nb.data = d;
    }
    /* ── A PLACED FEATURED BLOCK DEFAULTS TO EXPLORE HOMES (2026-08-21, Jack:
       "should default to homes"). The Table Header Bar ships "Explore Homes", and
       the block opened on Explore Floorplans — the section's two surfaces
       disagreeing at first paint. A block the level table covers starts at comms;
       templates, duplicates and saved docs carry their own stored level. */
    const nm = echNameOf(type);
    if (nm && ECH_FEAT.comms.fields[nm]) {
      nb.data = { ...(nb.data as Record<string, unknown>), __featLevel: "comms" };
    }
    /* the caller's own seeds win — the Featured Add card passes the current
       block's mode so a card added in Manual ARRIVES in Manual (2026-08-21,
       Jack: "it keeps switching to dynamic every time i tried to add a home —
       don't want that") */
    if (extra) nb.data = { ...(nb.data as Record<string, unknown>), ...extra };
    write((s) => {
      const out = s.slice();
      if (headBlock) out.splice(at, 0, headBlock, nb); else out.splice(at, 0, nb);
      return m2Live ? m2Tidy(out) : out;
    });
    setSelId(nb.id);
  };
  /* The "+" gap UI is gone (Jack 2026-08-21) but insertAt itself stays — the
     Featured section's capped Add card rides it (onAddBelow). Tests place blocks
     through this hook so they exercise the REAL insert semantics (listing blocks
     start empty, Featured defaults to comms) instead of hand-built state. */
  if (process.env.NODE_ENV === "test") {
    (window as unknown as { __esInsertAt: typeof insertAt }).__esInsertAt = insertAt;
  }
  /* ── M2 PANEL ACTIONS ─────────────────────────────────────────────────────────────
     A click adds the block under the selected one (the end when nothing is selected) —
     the monolith appended at the end; under the selection is where a builder is working.
     The toast is the monolith's sentence (esPal chip click, L13004). */
  const m2Add = (chipType: string) => {
    let type = chipType;
    const cur = docRef.current;
    const i = selId ? cur.findIndex((b) => b.id === selId) : -1;
    const before = cur.length;
    /* ── A BLOCK ADDED FROM THE PANEL IS THE SAME BLOCK THE TEMPLATE WOULD HAVE PLACED ──
       (Jack, 2026-10-06: "the exact replica of SFMC being pulled in like how the existing
       set up does with blocks and templates"). Measured live on all 7 templates: the
       markup was already Layne's block byte for byte, but 20 of 91 blocks differed in
       content, for two reasons — both fixed here, both for panel adds only:
         · the template's own per-block presets (`set` on its ECH_TPL entry: the CTA label
           "Join VIP List¹", Body Copy's empty headline, the Grand Opening body) were not
           applied. When a template is open and declares this block, the next unused
           occurrence's presets are applied — the same `set` echDoc applies.
         · `insertAt` starts a placed Featured card at community level ("comms", for the
           Featured Add card); the template path leaves the level unset, so its cards fill
           with the community's floorplans. A panel add leaves it unset too. */
    /* A FAMILY chip places the member the open template uses (Coming Soon's CTA row is
       ECH_2CTA, so "CTA Buttons" arrives as two buttons with "Join Interest List¹"),
       and the family default only when the template has none of them. */
    const extra: Record<string, unknown> = { __featLevel: undefined };
    if (echTpl) {
      const seq = echSeq(echTpl) || [];
      const fam = m2FamilyOf(echNameOf(type));
      const inFam = (n: string) => (fam ? fam.members.some(([m]) => m === n) : n === echNameOf(type));
      const hits = seq.filter((e) => inFam(e.block));
      if (hits.length) {
        const already = cur.filter((b) => inFam(echNameOf(b.type))).length;
        const e = hits[Math.min(already, hits.length - 1)];
        const pulled = echByName(e.block);
        if (pulled) type = pulled.key;
        if (e.set) Object.assign(extra, e.set);
      } else if (fam && m2r) {
        /* phase 2: the template doesn't use this family — its `familyDefault` decides which
           member arrives (Layne: "from SFMC"); without one, the family's first member as before */
        const def = m2FamilyDefault(m2r, fam.members.map(([m]) => m));
        const pulled = def ? echByName(def) : null;
        if (pulled) type = pulled.key;
      }
    }
    /* a block whose countAsOne group already has a run joins the END of that run, so a
       Featured card never starts a second run the module cap would count (Layne, 2026-10-15) */
    const runAt = m2Live && m2r ? m2RunEnd(cur, m2NameOf, m2r, echNameOf(type)) : -1;
    insertAt(runAt > -1 ? runAt : (i < 0 ? cur.length : i + 1), type, extra);
    if (docRef.current.length > before) toast(blockLabel(type) + " added \u2014 click text to edit, or use the panel");
  };
  const m2PickTemplate = (name: string) => {
    const t = echTplFor(name);
    /* the template already on the form: rebuild it here, since the form will not change */
    if (t && t === echTpl) {
      const d = echDoc(t, makeBlock);
      if (d && d.length) { echStamp(d, t, echVer); write(() => d); setSelId(null); if (onResetHistory) onResetHistory(); }
      return;
    }
    if (onPickTemplate) onPickTemplate(name);
  };
  /** drag-drop: move `fromId` to insertion index `idx` (monolith stage drop, move branch) */
  const moveTo = (fromId: string, idx: number) =>
    setBlocks((s) => {
      const from = s.findIndex((b) => b.id === fromId);
      if (from < 0) return s;
      /* the drop line's index counts the document WITH the dragged block still in it, so
         a downward move is one too many once it leaves — the same -1 the old splice did */
      const to = from < idx ? idx - 1 : idx;
      if (free) return freeMove(s, fromId, to);
      if (m2Live && m2r && !m2CanMove(s, m2NameOf, m2r, fromId, to)) { toast(M2_LOCKED_MSG); return s; }
      const r = reorder(s, fromId, to, echTplName);
      if (r.message) { toast(r.message); return s; }
      /* a header run moves as one (echPlacement.headerRun), so the play area is re-checked on
         the RESULT: the clicked block's own index can pass while the run lands outside it */
      if (m2Live && m2r && !m2SameLocked(s, r.doc as AnyBlockInstance[], m2NameOf, m2r)) { toast(M2_LOCKED_MSG); return s; }
      const fullWhy = m2Live && m2r ? m2ModulesBlocked(s, r.doc as AnyBlockInstance[], m2NameOf, m2r) : null;
      if (fullWhy) { toast(fullWhy); return s; }
      return m2Tidy(r.doc as AnyBlockInstance[]);
    });

  /* ── stage drag-and-drop (monolith wireStageDnD): the insertion index comes from
     the block midpoints under the pointer; the .esdrop line at that index lights up.
     Only canvas-block MOVES remain — palette-chip drags left with the palette. ── */
  const dragActive = !!dragId || !!palType;
  const stageOver = (e: React.DragEvent) => {
    if (!dragActive) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = palType ? "copy" : "move";
    const host = canvasRef.current;
    if (!host) return;
    const mods = Array.from(host.querySelectorAll(".esmod"));
    let idx = blocks.length;
    for (let i = 0; i < mods.length; i++) {
      const r = mods[i].getBoundingClientRect();
      if (e.clientY < r.top + r.height / 2) { idx = i; break; }
    }
    if (dropIdx !== idx) setDropIdx(idx);
  };
  const stageDrop = (e: React.DragEvent) => {
    if (!dragActive) return;
    e.preventDefault();
    const idx = dropIdx == null ? blocks.length : dropIdx;
    if (palType) insertAt(idx, palType);
    else if (dragId) moveTo(dragId, idx);
    setDragId(null); setPalType(null); setDropIdx(null);
  };
  const stageLeave = (e: React.DragEvent) => {
    if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null)) setDropIdx(null);
  };

  /* ⟳ Refresh blocks from SFMC — the menu item and its `refreshBlocks` handler were
     REMOVED per Jack (2026-09-18, pilot round): a requester never needs to re-pull the
     library by hand (it loads fresh on mount, and `sfmcBlocksWatch` keeps it current).
     Layne's "I just edited a block" path is a reload. */

  /* Copy HTML (esCopyHtml) and ☷ Copy AMPscript (esCopyAmp) REMOVED per Jack
     (2026-08-21). The AMPscript deliverable itself is unaffected — `echAmp` still
     powers the submit path and its tests; only the hand-copy menu items are gone. */

  /* ★ Save favorite (monolith esFav, 2026-07-22): export → /fav-save (R2 blob +
     "Division Favorites" ledger); same name re-saved = version bump.
     MVP-ONLY (Jack 2026-08-21: "can only save … related to the MVP set up"): a
     bespoke/template-edit document is refused — only the block build saves, and
     its exportHtml carries the ECH manifest, which is what the favorites gallery
     now requires to LIST a Studio-build favorite. The template name is recorded
     as provenance. */
  const saveFavorite = () => {
    if (!division) { toast("Pick a division on Step 1 first."); return; }
    if (editing) { toast("Only builds from the MVP templates can be saved as favorites."); return; }
    const html = blocks.length ? exportHtml : "";
    if (!html || html.length < 200) { toast("Nothing to save yet — build something first."); return; }
    const defName = projectName || "Untitled build";
    const name = window.prompt("Save as a " + division + " division favorite — name:", defName);
    if (!name) return;
    toast("Saving favorite…");
    favSave({ div: division, name, html, source: "Studio build", baseTemplate: echTpl || "" })
      .then((d) => toast(d && d.ok ? "Saved ★ " + name + " (v" + d.version + ") — reusable from the template picker." : "Save failed: unknown"))
      .catch(() => toast("Save failed — network."));
  };

  /* ── 💾 Save as draft · 🔗 Copy preview link (2026-09-18, pilot item 1) ────────────
     Both go through App (`namedDrafts.save`): the Studio has the HTML, App has the
     form + audience + blocks that make the draft resumable. The modal only collects
     the name — default = project name, or the name this canvas was opened under.
     "Copy preview link" is a save with the current name followed by a clipboard
     write, so the link a reviewer already holds shows THIS version (Jack: live). */
  const [draftOpen, setDraftOpen] = useState<null | { thenCopy: boolean }>(null);
  const [draftName, setDraftName] = useState("");
  const [draftBusy, setDraftBusy] = useState(false);
  const draftHtml = () => (editing ? (tplEdited || tplHtml) : (blocks.length ? exportHtml : ""));
  const copyText = async (t: string): Promise<boolean> => {
    try { if (navigator.clipboard && navigator.clipboard.writeText) { await navigator.clipboard.writeText(t); return true; } } catch { /* fall through */ }
    try { const ta = document.createElement("textarea"); ta.value = t; ta.style.position = "fixed"; ta.style.opacity = "0"; document.body.appendChild(ta); ta.select(); const ok = document.execCommand("copy"); ta.remove(); return ok; } catch { return false; }
  };
  const openDraftModal = (thenCopy: boolean) => {
    if (!namedDrafts) return;
    if (!division) { toast("Pick a division on Step 1 first."); return; }
    if (!draftHtml() || draftHtml().length < 200) { toast("Nothing to save yet — build something first."); return; }
    setDraftName(namedDrafts.current?.name || projectName || "");
    setDraftOpen({ thenCopy });
  };
  const runDraftSave = async (name: string, thenCopy: boolean) => {
    if (!namedDrafts) return;
    const nm = name.trim();
    if (!nm) { toast("Give the draft a name."); return; }
    setDraftBusy(true);
    toast(thenCopy ? "Saving and copying the preview link…" : "Saving draft…");
    type SaveR = { ok: boolean; previewUrl?: string; error?: string };
    const r: SaveR = await namedDrafts.save(nm, draftHtml()).catch((): SaveR => ({ ok: false, error: "network" }));
    setDraftBusy(false);
    if (!r.ok) { toast("Save failed" + (r.error ? " — " + r.error : ".")); return; }
    setDraftOpen(null);
    if (thenCopy && r.previewUrl) {
      const ok = await copyText(r.previewUrl);
      toast(ok ? "Preview link copied — anyone with it sees this draft as last saved." : "Saved. Preview link: " + r.previewUrl);
    } else {
      toast("Draft saved — under Drafts in the template picker.");
    }
  };
  const copyPreviewLink = () => {
    if (!namedDrafts) return;
    if (namedDrafts.current) { runDraftSave(namedDrafts.current.name, true); return; }
    openDraftModal(true); /* first time: it needs a name — one modal, then the copy */
  };

  /* ── 5 MB size guidance chip (monolith esSizeSync) ── */
  const sizeMb = useMemo(() => {
    try {
      const html = editing ? (tplEdited || tplHtml) : exportHtml;
      const bytes = typeof Blob !== "undefined" ? new Blob([html]).size : html.length;
      return bytes / 1048576;
    } catch { return 0; }
  }, [editing, tplHtml, tplEdited, exportHtml]);

  /* ── zoom (monolith esZo/esZi/esZfit): manual .2–1.6; ✥ toggles fit ↔ 100% ── */
  const zOut = () => { setFitOn(false); setZoom((z) => Math.max(0.2, +(z - 0.1).toFixed(2))); };
  const zIn = () => { setFitOn(false); setZoom((z) => Math.min(1.6, +(z + 0.1).toFixed(2))); };
  const fit = () => {
    if (fitOn) { setFitOn(false); setZoom(1); return; }
    const cv = canvasRef.current; if (!cv) return;
    const paper = cv.querySelector(".paper, .tpledit-frame") as HTMLElement | null;
    const h = (paper && paper.scrollHeight) || 1, w = 600;
    const availH = cv.clientHeight - 36, availW = cv.clientWidth - 36;
    setZoom(Math.max(0.15, Math.min(1, +Math.min(availH / h, availW / w).toFixed(3))));
    setFitOn(true);
  };

  /* keyboard: ⌘Z / ⌘⇧Z / ⌘Y (ignored while typing). Escape never closes the Studio. */
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      /* an inline edit owns Escape (it cancels the edit, not the Studio) — its own
         handler stops propagation, this is the belt for a stray focus target */
      if (e.key === "Escape") {
        if (inlineRef.current) return;
        /* ── ESCAPE NO LONGER LEAVES DESIGN EMAIL (2026-09-23, Lauren Gleim, Form
           Feedback 155: "Pressing Escape closed Design Email with no warning. Ask to
           confirm or auto-save, so edits aren't lost."). Nothing was ever lost (the
           email lives in the app and is autosaved), but the exit was a surprise, and
           it skipped the Done button's own checks: the required-fields gate and the
           "Checking division data…" hold. Done and "Leave Studio" are the only ways
           out now. Escape still closes whatever is on top (pickers, dropdowns,
           pop-ups) through their own handlers, and still gives up a field's focus. */
        if (t && (/^(input|textarea|select)$/i.test(t.tagName) || t.isContentEditable)) t.blur();
        return;
      }
      /* …and ⌘Z must not undo the document while someone is typing ON the email */
      if (t && (/^(input|textarea|select)$/i.test(t.tagName) || t.isContentEditable)) return;
      const k = (e.key || "").toLowerCase();
      if ((e.ctrlKey || e.metaKey) && k === "z") { e.preventDefault(); if (e.shiftKey) onRedo(); else onUndo(); }
      else if ((e.ctrlKey || e.metaKey) && k === "y") { e.preventDefault(); onRedo(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onUndo, onRedo]);

  /* the "… More" menu closes on any outside click */
  useEffect(() => {
    if (!moreOpen) return;
    const close = () => setMoreOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [moreOpen]);

  /* the block Inspector — one definition, used by Design Email and by proofing's Fields tab */
  const inspFor = (sel: AnyBlockInstance) => (
    <Inspector block={sel} key={sel.id} reqKeys={new Set(reqArmed ? reqBlanks.filter((r) => r.id === sel.id).map((r) => r.k) : [])} onData={(p) => patch(sel.id, p)} onOpenBynder={onOpenBynder} gr={grFor(sel.type)} onDelete={() => del(sel.id)} deleteWhy={free ? undefined : controlState(blocks as unknown as (PlaceBlock & CapBlock)[], sel.id, echTplName).delete} echCtx={echCtx} doc={blocks} onFlash={grFlash} onToast={toast} onSwap={(type, data) => swapBlock(sel.id, type, data)} onSwapMany={swapBlocks} onReplaceSection={replaceSection} onPacks={addPickPacks} onAddBelow={() => { const i = blocks.findIndex((x) => x.id === sel.id); if (i > -1) insertAt(i + 1, sel.type, { __echMode: (sel.data as Record<string, unknown>).__echMode ?? "manual", ...echCarryLabels(sel.data as Record<string, unknown>, sel.type) }); }} onInsertBelow={(type, extra) => { const i = blocks.findIndex((x) => x.id === sel.id); if (i > -1) insertAt(i + 1, type, extra); }} onSelectBlock={(id) => { patch(id, { __echMode: "manual" }); setSelId(id); }} onDeleteBlock={(id) => del(id)} onMoveBlock={(fromId, idx) => moveTo(fromId, idx)} m2={m2On} />
  );

  return (
    <div className={"es on" + (proof ? " esproof " + (pc || pui.adding ? "pcomment" : "pedit") + (pui.hlPick ? " phlpick" : "") + (pui.hlText ? " phltext" : "") : "")}>
      {proof && <ProofStrip p={proof} division={division} title={projectName} />}
      {/* top bar — monolith order (L10597): title (+MVP badge) · Desktop/Mobile
          LEFT beside the title (2026-07-23, per Jack) · right group: undo/redo ·
          zoom · size chip · "… More" · Done */}
      <div className="esbar">
        {/* the monolith's "MVP · 5 TEMPLATES" badge is intentionally NOT ported —
            removed per Jack 2026-08-08 (see the per-block inspector audit doc) */}
        <div className="estitle">
          <span className="dot" />{proof ? (pc ? "Review Email" : "Edit Email") : "Design Email"}
        </div>
        <div className="esdev esdev-left">
          <button className={device === "desktop" ? "on" : ""} onClick={() => setDevice("desktop")}>Desktop</button>
          <button className={device === "mobile" ? "on" : ""} onClick={() => setDevice("mobile")}>Mobile</button>
        </div>
        {proof && <ProofCompareSeg p={proof} ui={pui} />}
        <div className="esright">
          {!pc && <button className="eszb esundo" title="Undo (⌘Z)" disabled={!canUndo} onClick={onUndo}>↶</button>}
          {!pc && <button className="eszb esundo" title="Redo (⌘⇧Z)" disabled={!canRedo} onClick={onRedo}>↷</button>}
          <button className="eszb" onClick={zOut}>−</button>
          <span className="eszr">{Math.round(zoom * 100)}%</span>
          <button className="eszb" onClick={zIn}>+</button>
          <button className={"eszb" + (fitOn ? " on" : "")} title="Fit whole email / back to 100%" onClick={fit}>✥</button>
          {sizeMb >= 5 && (
            <span className="essize" title="Over the 5 MB guidance — downsize or remove an image.">
              {(sizeMb >= 10 ? Math.round(sizeMb) : sizeMb.toFixed(1))} MB · over limit
            </span>
          )}
          {proof ? (
            <ProofBarRight p={proof} ui={pui} blocks={blocks} onCopy={(text, msg) => {
              try { navigator.clipboard.writeText(text).then(() => toast(msg), () => toast(text)); } catch { toast(text); }
            }} />
          ) : (<>
          <div className="esmorewrap">
            <button className="estb" title="More actions" onClick={(e) => { e.stopPropagation(); setMoreOpen(!moreOpen); }}>… More</button>
            <div className={"esovm" + (moreOpen ? " on" : "")} onClick={(e) => e.stopPropagation()}>
              <button className="esovi" title="Save this build as a division favorite — reusable from the template picker" onClick={() => { setMoreOpen(false); saveFavorite(); }}>★ Save favorite</button>
              {/* Copy HTML and ☷ Copy AMPscript REMOVED per Jack (2026-08-21) — divisions
                  shouldn't hand-carry markup; the build travels via submit / favorites.
                  INTENTIONAL DIVERGENCE from the monolith — do not "fix" them back. */}
              {/* ⟳ Refresh blocks from SFMC REMOVED per Jack (2026-09-18, pilot round):
                  a requester never needs it — the library loads fresh on mount. */}
              {namedDrafts && (
                <>
                  <button className="esovi" data-testid="save-draft" title="Keep working on this later — it shows under Drafts in the template picker" onClick={() => { setMoreOpen(false); openDraftModal(false); }}>💾 Save as draft</button>
                  <button className="esovi" data-testid="copy-preview-link" onClick={() => { setMoreOpen(false); copyPreviewLink(); }}>🔗 Copy preview link</button>
                </>
              )}
            </div>
          </div>
          {reqBlanks.length > 0 && !editing && reqArmed ? (
            <div className="esreqwrap">
              <button className="esdone esreq" data-testid="req-gate" onClick={() => setReqOpen(!reqOpen)}>
                ⚠ {reqBlanks.length} required missing →
              </button>
              {reqOpen && (
                <div className="esreqpop" data-testid="req-pop">
                  <h5>Required before Done</h5>
                  {/* GROUPED BY BLOCK (2026-08-21, Jack picked option 2 off the layout
                      mockups: "i like the color look but the text format is funky") —
                      each block named ONCE, its missing fields indented under it, and
                      the per-home link flags ROLLED UP to one line per table ("don't
                      red flag in this section, just in the main section" — the
                      per-home red lives on the cards). Parentheticals fold onto the
                      line after a middot so nothing wraps the Fix column. */}
                  {(() => {
                    interface Grp { id: string; block: string; items: { k: string; field: string }[]; homes: number }
                    const groups: Grp[] = [];
                    const byId: Record<string, Grp> = {};
                    reqBlanks.forEach((r) => {
                      let g = byId[r.id];
                      if (!g) { g = { id: r.id, block: r.block, items: [], homes: 0 }; byId[r.id] = g; groups.push(g); }
                      if (/^home\d+Url$/.test(r.k)) g.homes++;
                      /* a `data` row's `field` is Layne's WHOLE sentence, and her
                         `invalidValue` one ends with the offending value in parentheses
                         — the parenthetical fold below is for a cap's "(over by 12)" and
                         would rewrite her copy into "· https://…". So it is skipped
                         here, deliberately (2026-09-09). */
                      else if (r.kind === "data") g.items.push({ k: r.k, field: r.field });
                      else g.items.push({ k: r.k, field: r.field.replace(/\s*\(([^)]+)\)/, " · $1") });
                    });
                    /* ── TWO CARDS, ONE NAME: SAY WHICH (2026-09-10, NSH: "Floorplan URL shown
                       as missing a URL but there is a populated URL") ──────────────────────
                       A template with three Featured cards lists three groups all titled
                       "Featured Plan", so the requester opens the card they can see (full
                       URL) while the flag belongs to a sibling. When a label repeats, each
                       group carries its position among the blocks of that type, and the
                       card's own title when it has one. Jump still lands on the right block. */
                    const dupLabel: Record<string, number> = {};
                    groups.forEach((g) => { dupLabel[g.block] = (dupLabel[g.block] || 0) + 1; });
                    const ordinal: Record<string, number> = {};
                    groups.forEach((g) => {
                      if (dupLabel[g.block] < 2) return;
                      const b = blocks.find((x) => x.id === g.id);
                      const sameType = blocks.filter((x) => b && x.type === b.type);
                      const n = b ? sameType.indexOf(b) + 1 : (ordinal[g.block] = (ordinal[g.block] || 0) + 1);
                      const title = String((b && (b.data as Record<string, unknown>).PlanName) || "").trim();
                      g.block = g.block + " " + n + (title ? " · " + title : "");
                    });
                    return groups.map((g) => (
                      <div key={g.id} className="esreqblk">
                        <div className="esreqbn">{g.block}</div>
                        {g.items.map((it) => (
                          <button key={it.k} className="esreqrow" onClick={() => jumpToReq(g.id)}>
                            <span className="dot" /><span className="fld">{it.field}</span><span className="go">Fix →</span>
                          </button>
                        ))}
                        {g.homes > 0 && (
                          <button className="esreqrow" data-testid="req-homes" onClick={() => jumpToReq(g.id)}>
                            <span className="dot" /><span className="fld">{g.homes} home{g.homes > 1 ? "s" : ""} need{g.homes > 1 ? "" : "s"} a View Home link</span><span className="go">Fix →</span>
                          </button>
                        )}
                      </div>
                    ));
                  })()}
                  {/* the gate must never TRAP anyone in the Studio (Jack, same day:
                      "should have an option to leave") — the build keeps its edits,
                      the blanks stay counted, and the form's submit belt still
                      catches them before anything files */}
                  <button className="esreqleave" data-testid="req-leave" onClick={() => { setReqOpen(false); onClose(); }}>
                    Leave Studio — Finish later
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="esdone" data-testid="es-done" disabled={doneWaiting || dataPending} onClick={() => {
              /* the gate's answer has not arrived — never close on a verdict we do not
                 have yet (2026-09-09) */
              if (dataPending) return;
              /* the feed is mid-fill: hold the click, the effect above decides when it lands */
              if (feedPending && !editing) { setDoneWaiting(true); return; }
              /* the arming click: Done with blanks lights everything up instead of closing */
              if (reqBlanks.length > 0 && !editing) { setReqArmed(true); setReqOpen(true); return; }
              onClose();
            }}>{dataPending ? "Checking division data…" : "Done"}</button>
          )}
          </>)}
        </div>
      </div>

      {/* ── WHAT MOVED SINCE THE ORIGINAL SEND (duplicate only) ────────────────
          A direct child of `.es`, which is the column — NOT a child of `.esstage`
          or `.esbuild`, both of which are flex ROWS, where a banner would have sat
          beside the canvas as a narrow left column instead of above it.

          Not inside the canvas either: the canvas is the email, and nothing that is
          not the email belongs on it (standing rule — no explanatory prose on a
          built surface). This is the one read-out Jack asked for, and it is
          temporary by construction: only differences are listed, it appears only on
          a duplicate, and "Got it" removes it for good. */}
      {!!(dupNotices && dupNotices.length) && (
        <div className="esdupnote" data-testid="dup-notices">
          <div className="esdupnote-hd">
            <b>{dupNotices.length === 1 ? "One thing changed" : dupNotices.length + " things changed"} since this email went out</b>
            <button onClick={onClearDupNotices} title="Hide these notes">Got it</button>
          </div>
          <ul>
            {dupNotices.map((n, i) => <li key={i} className={"k-" + n.kind}>{n.msg}</li>)}
          </ul>
        </div>
      )}

      <div className="esstage">
        <div className="esbuild">
          {/* no left palette in the MVP (monolith html.mvpaud .es .espal{display:none}).
              MILESTONE 2 brings it back behind `m2On` only — every block in Layne's
              library, click to add under the selected block, drag to drop anywhere. */}
          {m2On && !editing && !echPending && (
            <M2Palette
              groups={palGroups}
              tplName={echTpl || echTplName || ""}
              tplOrder={m2TplOrder}
              templates={m2Templates}
              onPickTemplate={m2PickTemplate}
              q={palQ} setQ={setPalQ}
              onAdd={m2Add}
              onDragType={(t) => { setPalType(t); if (!t) setDropIdx(null); }}
            />
          )}

          {/* ── canvas ── */}
          <div className={"escanvas" + (proof && (pui.cmp || pui.view) ? " pvmode" : "")} ref={canvasRef} onDragOver={stageOver} onDrop={stageDrop} onDragLeave={stageLeave}>
            {proof && (pui.cmp || pui.view) ? (
              /* a saved version open read-only, or two side by side (M2 Discovery, 2026-10-12) */
              <ProofVersionCanvas p={proof} ui={pui} />
            ) : echPending ? (
              /* cold browser, template requested, library still in flight — the one
                 state that is neither the template editor nor an empty canvas */
              <div className="espaper">
                <div className="paper">
                  <div className="esempty esloading" data-testid="ech-loading">
                    <div className="big">Getting the blocks from Marketing Cloud…</div>
                    <div className="sm">{echTplName} is built from Layne&rsquo;s Content Builder blocks. The first load of the day takes a moment; after that it opens straight away.</div>
                  </div>
                </div>
              </div>
            ) : editing ? (
              /* same scaled-paper model as block mode — the bare full-width
                 estpl-edit ignored the zoom controls entirely (Jack 2026-08-09) */
              <div className="espaperbox">
                <div className="espaper" style={{ transform: `scale(${zoom})` }}>
                <div className="estpl-edit">
                  <TemplateEditor html={tplHtml} onSerialized={setTplEdited}
                    onSelect={(s) => { setTplSel(s); if (s) setInspOpen(true); }} apiRef={tplApi}
                    caps={tplCaps} lockArt={!!resend} />
                </div>
                </div>
              </div>
            ) : (
            <div className="espaperbox" ref={paperBoxRef}>
            <div className="espaper" ref={paperInnerRef} style={{ transform: `scale(${zoom})` }}>
              <div className={"paper" + (device === "mobile" ? " mobile" : "")}>
                {blocks.length === 0 && (
                  <div className="esempty"><div className="big">Your email is empty</div><div className="sm">Drag a block or module here, click one on the left, or pick a template above.</div></div>
                )}
                {blocks.map((b, i) => (
                  <Fragment key={b.id}>
                    <div className={"esdrop" + (dropIdx === i ? " show" : "")} />
                    <div
                      className={"esmod" + (selId === b.id ? " sel" : "") + (dragId === b.id ? " dragging" : "") + (inlineId === b.id ? " editing" : "") + (reqIds.has(b.id) ? " echneeds" : "")}
                      data-id={b.id}
                      data-type={b.type}
                      /* ── SHORT BLOCKS LIFT THEIR CHROME OFF THE CONTENT (2026-08-21).
                         Jack, on a selected Table Header Bar: "why am I still seeing it
                         on desktop" — the name pill and the ↑↓⧉✕ toolbar overlay a
                         block's top corners, and on a 60px bar that covers the headline
                         and the View All link. The email is untouched; it is pure editor
                         chrome — so on any block under 96px the chrome floats ABOVE the
                         block instead. Measured on hover/select (the only times the
                         chrome is visible), so a block that grows or shrinks re-answers. */
                      onMouseEnter={(e) => { const el = e.currentTarget; el.classList.toggle("short", el.offsetHeight < 96); }}
                      /* the monolith sets `w.draggable=false` for the duration of an
                         inline edit — a draggable ancestor steals the text caret.
                         ALSO false for a PINNED block (2026-08-27): Layne asked for the
                         handle to be disabled rather than the move rejected afterwards —
                         "ideally the drag handle is disabled rather than the move being
                         rejected after the fact". `reorder` still refuses on the ↑↓ path,
                         which is the belt behind this brace. */
                      draggable={!pc && !pui.adding && !pui.hlPick && !pui.hlText && inlineId !== b.id && !pinnedIds.has(b.id)}
                      onClick={(e) => {
                        /* PROOFING, 10-26: "Highlight a block" armed — this click marks the block
                           for the division instead of selecting it */
                        if (proof && pui.hlPick) {
                          e.preventDefault();
                          toggleBlockHl(proof, blocks, b.id);
                          pui.setHlPick(false);
                          return;
                        }
                        /* "Highlight text" armed: a click here is the end of a word selection */
                        if (proof && pui.hlText) { if ((e.target as HTMLElement).closest?.("a")) e.preventDefault(); return; }
                        /* PROOFING: a reviewer's click drops a pin where they clicked; the
                           email itself is read-only for them */
                        if (pc || pui.adding) {
                          e.preventDefault();
                          if (proof?.data.can?.comment) { pui.setFocus(null); pui.setDraft(dropAt(e, blocks, b.id)); pui.setAdding(false); if (pc) setInspOpen(true); }
                          return;
                        }
                        if (proof) { pui.setDraft(null); pui.setPanel(null); }
                        /* `.mi` is pointer-transparent no longer (see creative-studio.css),
                           so a click can land on a real <a> in the email — selecting the
                           block must never navigate the app away */
                        if ((e.target as HTMLElement).closest?.("a")) e.preventDefault();
                        /* selection shows the chrome too — re-measure (see onMouseEnter) */
                        const el = e.currentTarget as HTMLElement;
                        el.classList.toggle("short", el.offsetHeight < 96);
                        setSelId(b.id); setInspOpen(true); /* monolith esSelect adds insp-open */
                      }}
                      onDoubleClick={(e) => { if (!pc && !pui.hlText && !pui.hlPick) startInline(b, e); }}
                      onDragStart={(e) => { setDragId(b.id); e.dataTransfer.effectAllowed = "move"; }}
                      onDragEnd={() => { setDragId(null); setDropIdx(null); }}
                    >
                      <div className="mtag">{blockLabel(b.type)}</div>
                      {/* ── DON'T OFFER THE ACTION, THEN REJECT IT (Layne, 2026-09-03) ──────────
                          `controlState` dry-runs the SAME `reorder` / `capViolation` the handlers
                          call and hands back the reason a control is dead; the button shows it
                          as the tooltip and reads as disabled. It stays clickable on purpose: a
                          truly disabled button swallows the hover in some browsers, and the
                          click on a dead control repeats the reason as the toast — the same
                          sentence, so nothing can disagree. The handlers still run the real
                          check, so a stale render never lets a refused action through. */}
                      {(() => {
                        /* guardrails off: only the two edges of the document can stop an arrow */
                        const cs0 = free
                          ? { up: i === 0 ? "Already the first block." : undefined, down: i === blocks.length - 1 ? "Already the last block." : undefined, dup: undefined, delete: undefined as string | undefined }
                          : controlState(blocks as unknown as (PlaceBlock & CapBlock)[], b.id, echTplName);
                        /* phase 2: an arrow that would leave the play area is dead, with the same sentence the handler toasts */
                        const managed = m2ManagedIds.has(b.id);
                        const cs = m2Live && m2r ? {
                          ...cs0,
                          up: managed ? M2_LOCKED_MSG : cs0.up || (m2CanMove(blocks, m2NameOf, m2r, b.id, i - 1) ? undefined : M2_LOCKED_MSG),
                          down: managed ? M2_LOCKED_MSG : cs0.down || (m2CanMove(blocks, m2NameOf, m2r, b.id, i + 1) ? undefined : M2_LOCKED_MSG),
                          dup: managed ? M2_LOCKED_MSG : cs0.dup,
                        } : cs0;
                        /* main #354: a pinned block's ✕ is dead with the rule's reason — kept under M2's own lock */
                        const delWhy = managed ? M2_LOCKED_MSG : cs0.delete;
                        const btn = (why: string | undefined, label: string, glyph: string, go: () => void) => (
                          <button className={why ? "off" : undefined} aria-disabled={why ? true : undefined}
                            title={why || label}
                            onClick={(e) => { e.stopPropagation(); if (why) { toast(why); return; } go(); }}>{glyph}</button>
                        );
                        return (
                          <div className="mbar">
                            {btn(cs.up, "Up", "↑", () => move(b.id, -1))}
                            {btn(cs.down, "Down", "↓", () => move(b.id, 1))}
                            {btn(cs.dup, "Duplicate", "⧉", () => dup(b.id))}
                            <button className={"del" + (delWhy ? " off" : "")} aria-disabled={delWhy ? true : undefined} title={delWhy || "Delete"}
                              onClick={(e) => { e.stopPropagation(); if (delWhy) { toast(delWhy); return; } del(b.id); }}>✕</button>
                          </div>
                        );
                      })()}
                      <div className="mi" dangerouslySetInnerHTML={htmlOf(b)} />
                      {/* editor-only; see amenNoteOf — never reaches the email */}
                      {(() => { const note = amenNoteOf(b); return note ? <div className="esamennote" data-testid="amen-empty-note">{note}</div> : null; })()}
                    </div>
                  </Fragment>
                ))}
                <div className={"esdrop" + (dropIdx === blocks.length ? " show" : "")} />
              </div>
              {proof && <ProofPins p={proof} ui={pui} blocks={blocks} paperRef={paperInnerRef} zoom={zoom} />}
            </div>
            </div>
            )}
          </div>

          {/* ── TEMPLATE SECTION inspector (editing mode) — the monolith's MVP
              template stage DOES have an esinsp (captured live 2026-08-09; the
              old "edit stage has no esinsp" note was wrong — that was the
              sent-email ext editor). Empty state + per-section cards per the
              2026-08-09 audit inventory. ── */}
          {editing && inspOpen && (
            <>
              <div className="esresize" title="Drag to resize — double-click to reset"
                onMouseDown={resStart} onDoubleClick={() => { setInspW(378); try { localStorage.removeItem("esInspW"); } catch { /* noop */ } }} />
              <aside className="esinsp" style={{ width: inspW }}>
                {tplSel ? (
                  <TplInspector key={tplSel.idx + ":" + tplSel.label} sel={tplSel} api={tplApi} onOpenBynder={onOpenBynder}
                    onRefresh={setTplSel} division={division || ""}
                    onClose={() => { setInspOpen(false); tplApi.current?.deselect(); setTplSel(null); }} />
                ) : (
                  <>
                    <div className="ih">
                      <span className="ihtx">Inspector<small>Nothing selected</small></span>
                      <button className="ihx" title="Hide panel" onClick={() => setInspOpen(false)}>✕</button>
                    </div>
                    {/* monolith empty-state copy, verbatim */}
                    <div className="esemptyi"><div className="ic">✎</div>Click any section of the email — image, headline, floorplans, listing — to edit it or switch it to Dynamic.</div>
                  </>
                )}
              </aside>
            </>
          )}

          {/* ── block inspector — CLOSED by default (monolith insp-open gating): a block
              select or the gold EDIT tab opens it; × closes without deselecting. ── */}
          {/* PROOFING: comments live in a LEFT panel. Review view: that's the only panel. Edit
              view (Jack, 2026-10-07): comments stay on the left, always open, and the app's own
              block inspector sits on the right exactly as in Design Email — full editing. */}
          {proof && !editing && (!pc || inspOpen) && (
            <aside className="esinsp pcm">
              <ProofInspector p={proof} ui={pui} blocks={blocks} sel={null}
                onDeselect={() => { setSelId(null); pui.setFocus(null); }} fields={null} />
            </aside>
          )}
          {(!proof || !pc) && !editing && inspOpen && !(proof && (pui.cmp || pui.view)) && (
            <>
              <div className="esresize" title="Drag to resize — double-click to reset"
                onMouseDown={resStart} onDoubleClick={() => { setInspW(378); try { localStorage.removeItem("esInspW"); } catch { /* noop */ } }} />
              <aside className="esinsp" style={{ width: inspW }}>
                {sel ? (
                  (() => {
                    /* monolith ih: dot + label + "Block · N items" count from the first
                       repeat/listrows field */
                    const rf = (blockDef(sel.type)?.fields || []).find((f) => f.type === "repeat" || f.type === "listrows");
                    const cnt = rf
                      ? rf.type === "repeat"
                        ? (Array.isArray(sel.data[rf.k!]) ? (sel.data[rf.k!] as unknown[]).length : 0)
                        : String(sel.data[rf.k!] ?? "").split("\n").filter((l) => l.trim()).length
                      : 0;
                    return (
                      <>
                        <div className="ih">
                          <span className="ihdot" />
                          {/* the head reads the CHOSEN size once a block carries one — "Main Image (600×300)",
                              not the header's fixed "(500x350)" (2026-09-17, services/imgSize.ts) */}
                          <span className="ihtx">{labelWithSize(blockLabel(sel.type), (() => { const fs = blockDef(sel.type)?.fields || []; const ks = imgSizeKeys(fs); const im = fs.find((f) => f.type === "image"); const dd = (sel.data || {}) as Record<string, unknown>; return imgSizeSlot(dd, ks, undefined, ks && !ks.w ? ((im && im.w) || markupImgWidth(dd.__html, (im && im.k) || "imgSrc")) : 0); })())}<small>{"Block" + (cnt ? " · " + cnt + " item" + (cnt === 1 ? "" : "s") : "")}</small></span>
                          <button className="ihx" title="Hide panel" onClick={() => setInspOpen(false)}>✕</button>
                        </div>
                        <div className="ib">
                          {/* echCtx + the document are what a dynamic ECH field resolves
                              against — without them the panel cannot show the feed's value,
                              cannot say which level it came from, and cannot hand a field
                              back when the user clears it (code review #1/#3). */}
                          {isDisclaimerBlock(sel.type) && disclaimerEditable && !(proof && !proof.data.can?.editContent) && (
                            <div className="esdslive" data-testid="ds-edit">
                              <label className="esdslive-lbl" htmlFor="esdsedit">Disclaimer text</label>
                              <textarea id="esdsedit" className="esdslive-edit" value={dsOverride(sel) || dsParas.join("\n\n")}
                                onChange={(e) => patch(sel.id, { __dsText: e.target.value })} />
                            </div>
                          )}
                          {isDisclaimerBlock(sel.type) && !disclaimerEditable && (dsParas.length > 0 || !!dsOverride(sel)) && (
                            <div className="esdslive" data-testid="ds-live">
                              <span className="esdslive-pill">🔒 {dsOverride(sel) ? "Edited in markup" : "Built from Step 4"}</span>
                              <div className="esdslive-text">{(dsOverride(sel) ? dsOverride(sel).split(/\n\s*\n/).filter((p) => p.trim()) : dsParas).map((p, i) => <p key={i}>{p}</p>)}</div>
                            </div>
                          )}
                          {proof && !proof.data.can?.editContent ? <div className="ihint">This email is approved. Nothing can be edited.</div> : inspFor(sel)}
                        </div>
                      </>
                    );
                  })()
                ) : (
                  <>
                    <div className="ih">
                      <span className="ihtx">Inspector<small>Nothing selected</small></span>
                      <button className="ihx" title="Hide panel" onClick={() => setInspOpen(false)}>✕</button>
                    </div>
                    <div className="esemptyi"><div className="ic">✎</div>Click a block on the email to edit it.</div>
                  </>
                )}
              </aside>
            </>
          )}
        </div>
      </div>

      {/* the gold vertical EDIT tab on the right edge while the inspector is
          closed (monolith #esInspTab; hidden in edit mode) */}
      {!editing && !inspOpen && (
        <button className="esinsptab" onClick={() => setInspOpen(true)}>{pc ? "Comments ◂" : "Edit ◂"}</button>
      )}

      {/* the "Fix before sending — SFMC guardrails" card that used to render here is gone
          (2026-09-12): a hard violation is a row in the required popover now (see
          `hardRows`), so the Studio has one gate surface, not two. */}

      {/* AI feature removed for now (Jack 2026-08-09 pre-push review) — the monolith's
          esAIcopy "✦ AI copy" More-menu item and the full-scope AiCopyModal are
          intentionally not rendered. AiCopyModal stays on disk. */}

      {/* the red flash pill repeating the exact limit wording (monolith #grFlash) */}
      <div id="grFlash" className={"grflash" + (flashOn ? " on" : "")}>
        <span className="grflash-i">⚠</span>{flashMsg}
      </div>

      {/* toast (monolith #esToast) */}
      <div className={"estoast" + (toastOn ? " show" : "")}>{toastMsg}</div>
          {/* Save as draft — name it, done (mock screen 2). The division is read-only:
          a draft files under the Step-1 division so it lands in that folder. */}
      {draftOpen && namedDrafts && (
        <div className="rb-ovl" onClick={() => !draftBusy && setDraftOpen(null)}>
          <div className="rb-modal" style={{ width: "min(460px, 96vw)" }} onClick={(e) => e.stopPropagation()} data-testid="draft-modal">
            <div className="rb-modal-hd"><b>{draftOpen.thenCopy ? "Name the draft to share" : "Save as draft"}</b><button onClick={() => setDraftOpen(null)}>✕</button></div>
            <div style={{ padding: "16px 20px 20px" }}>
              <div className="fb-lab">Draft name</div>
              <input value={draftName} autoFocus onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") runDraftSave(draftName, draftOpen.thenCopy); }}
                style={{ width: "100%", border: "1px solid #d3d9e2", borderRadius: 10, padding: "10px 13px", font: "inherit", fontSize: 14, boxSizing: "border-box" }} />
              <div className="fb-lab" style={{ margin: "14px 0 6px" }}>Division</div>
              <div style={{ fontSize: 14, color: "#1C2430" }}>{division}</div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18 }}>
                <button type="button" className="btn ghost" disabled={draftBusy} onClick={() => setDraftOpen(null)}>Cancel</button>
                <button type="button" className="btn" disabled={draftBusy} onClick={() => runDraftSave(draftName, draftOpen.thenCopy)}>
                  {draftBusy ? "Saving…" : draftOpen.thenCopy ? "Save & copy link" : "Save draft"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
</div>
  );
}
